import {
  ComponentValidationError,
  DuplicateComponentError,
  UnknownPreservedComponentError,
  createComponent,
  createVersion,
  getValidationSummary,
  isValidationSuccess,
  resolveComponentVersion,
  validateCustomComponentProps
} from "./chunk-D5NB3VZH.js";
import {
  compareSemver,
  isValidSemver,
  latestVersion,
  parseSemver
} from "./chunk-244MHDOZ.js";
import {
  DEFAULT_ERROR_CONFIG,
  ERROR_EMOJIS,
  calculatePosition,
  clearComponentNamesCache,
  createErrorConfig,
  createJsonParseError,
  extractStandardComponentNames,
  formatErrorMessage,
  formatErrorSummary,
  getLiteralValue,
  getObjectSchemaPropertyNames,
  getSchemaMetadata,
  groupErrorsByPath,
  isLiteralSchema,
  isObjectSchema,
  isUnionSchema,
  transformValueError,
  transformValueErrors
} from "./chunk-LLBCT7WL.js";
import {
  FONT_URL_ALLOWLIST,
  WEIGHT_LABELS,
  detectFontFormat,
  isAllowedFontUrl,
  readFontFamilyNames,
  readNameRecords,
  rewriteFontFamilyName,
  standardSubfamilyNames,
  synthesizeFamilyName,
  validateFontMetadata
} from "./chunk-FXASCTRY.js";
import {
  assertNever
} from "./chunk-QQBR4CWS.js";
import {
  ASSET_UNREADABLE,
  FeatureRequirementCollector,
  RENDERER_DEPENDENCY_MISSING,
  RendererRegistry,
  UnsupportedRendererFeatureError,
  assertRendererSupports,
  assetUnreadableError,
  diagnoseUnsupportedFeatures,
  partitionDiagnostics,
  rendererError,
  rendererWarning
} from "./chunk-2LNIQEP4.js";
import {
  convertToJsonSchema,
  createComponentSchema,
  createComponentSchemaObject,
  exportSchemaToFile,
  fixSchemaReferences,
  restructureNameDiscriminatedUnions,
  unionBranches
} from "./chunk-LFHU4EOV.js";
import {
  CANVASES,
  ChromeSchema,
  DesignSpacingSchema,
  DesignSystemProperties,
  DesignSystemSchema,
  MotifSchema,
  PaletteSchema,
  ROLE_SCALE_STEPS,
  TYPE_ROLES,
  TextCaseSchema,
  TypeRoleNameSchema,
  TypeRoleSchema,
  TypeRolesSchema,
  TypographySchema,
  capsFormatting,
  designCanvas,
  designColors,
  resolveDesignColor,
  resolveMotif,
  resolveTypeRoles,
  typeScaleSizes,
  validateDesignColors
} from "./chunk-JQGFMWOO.js";
import {
  FontFamilyNameSchema,
  FontRegistryEntrySchema,
  FontRegistrySchema,
  FontSourceSchema,
  SAFE_FONTS,
  isSafeFont
} from "./chunk-6KUQYVPT.js";

// src/types/services.ts
var DEFAULT_VISUAL_DPI = 200;
var MIN_VISUAL_DPI = 36;
var MAX_VISUAL_DPI = 600;
function clampVisualDpi(dpi) {
  if (typeof dpi !== "number" || !Number.isFinite(dpi))
    return DEFAULT_VISUAL_DPI;
  return Math.min(MAX_VISUAL_DPI, Math.max(MIN_VISUAL_DPI, Math.round(dpi)));
}
var MAX_RASTERIZE_FONTS = 32;
var MAX_RASTERIZE_FONT_BYTES = 8 * 1024 * 1024;
var MAX_RASTERIZE_BATCH_SLIDES = 32;

// src/fonts/collect.ts
var FONT_NAME_KEYS = /* @__PURE__ */ new Set([
  "family",
  "fontFace",
  "titleFontFace",
  "legendFontFace",
  "dataLabelFontFace",
  "catAxisLabelFontFace",
  "valAxisLabelFontFace"
]);
var THEME_FONT_KEYS = /* @__PURE__ */ new Set(["heading", "body", "mono", "light"]);
var FONT_DECLARATION_KEYS = /* @__PURE__ */ new Set(["fontRegistry"]);
function collect(node, out, parentKey) {
  if (node == null) return;
  if (typeof node === "string") {
    if (parentKey && (FONT_NAME_KEYS.has(parentKey) || THEME_FONT_KEYS.has(parentKey))) {
      const trimmed = node.trim();
      if (trimmed.length > 0) out.add(trimmed);
    }
    return;
  }
  if (Array.isArray(node)) {
    for (const item of node) collect(item, out, parentKey);
    return;
  }
  if (typeof node === "object") {
    const maybeFonts = node.fonts;
    if (parentKey === "theme" && maybeFonts && typeof maybeFonts === "object") {
      for (const [k, v] of Object.entries(
        maybeFonts
      )) {
        if (typeof v === "string") {
          const trimmed = v.trim();
          if (trimmed.length > 0) out.add(trimmed);
        } else if (v && typeof v === "object") {
          const fam = v.family;
          if (typeof fam === "string" && fam.trim().length > 0) {
            out.add(fam.trim());
          }
        }
        void k;
      }
    }
    for (const [k, v] of Object.entries(node)) {
      if (FONT_DECLARATION_KEYS.has(k)) continue;
      collect(v, out, k);
    }
  }
}
function collectFontNames(doc) {
  const out = /* @__PURE__ */ new Set();
  collect(doc, out);
  return out;
}
var collectFontNamesFromDocx = collectFontNames;
var collectFontNamesFromPptx = collectFontNames;

// src/fonts/document-registry.ts
function isEntry(v) {
  if (!v || typeof v !== "object") return false;
  const e = v;
  return typeof e.id === "string" && typeof e.family === "string" && Array.isArray(e.sources);
}
function readAt(node, key) {
  if (!node || typeof node !== "object") return [];
  const raw = node[key];
  return Array.isArray(raw) ? raw.filter(isEntry) : [];
}
function documentFontRegistry(document) {
  if (!document || typeof document !== "object") return [];
  return readAt(document.props, "fontRegistry");
}
function themeFontRegistry(theme) {
  return readAt(theme, "fontRegistry");
}
function mergeFontRegistries(...groups) {
  const out = [];
  for (const group of groups) {
    for (const entry of group ?? []) {
      const family = entry.family.toLowerCase();
      const id = entry.id.toLowerCase();
      for (let i = out.length - 1; i >= 0; i--) {
        if (out[i].family.toLowerCase() === family && out[i].id.toLowerCase() === id) {
          out.splice(i, 1);
        }
      }
      out.push(entry);
    }
  }
  return out;
}

// src/fonts/validator.ts
function buildRegistryIndex(registeredEntries) {
  const idx = /* @__PURE__ */ new Set();
  for (const e of registeredEntries ?? []) {
    idx.add(e.family.toLowerCase());
    idx.add(e.id.toLowerCase());
  }
  return idx;
}
function validateFontReferences(input) {
  const registryIdx = buildRegistryIndex(input.registeredEntries);
  const resolved = [];
  const unresolved = [];
  const warnings = [];
  for (const name of input.referencedNames) {
    if (isSafeFont(name) || registryIdx.has(name.toLowerCase())) {
      resolved.push(name);
      continue;
    }
    unresolved.push(name);
    warnings.push({
      code: "FONT_UNRESOLVED",
      family: name,
      message: `Font "${name}" is not a SAFE_FONTS entry and is not registered via fonts.extraEntries. It will render with a host fallback on machines lacking the font. Safe fonts: ${SAFE_FONTS.join(", ")}.`
    });
  }
  return { resolved, unresolved, warnings };
}

// src/fonts/sources/data-loader.ts
var MAX_DATA_FONT_BYTES = 5 * 1024 * 1024;
function loadDataFontSource(input) {
  const raw = input.data.trim();
  let b64;
  if (raw.startsWith("data:")) {
    const comma = raw.indexOf(",");
    if (comma < 0) throw new Error("Invalid data URL: no payload separator");
    const header = raw.slice(5, comma);
    if (!header.includes(";base64")) {
      throw new Error("Data URL must be base64-encoded");
    }
    b64 = raw.slice(comma + 1);
  } else {
    b64 = raw;
  }
  const approxDecodedBytes = Math.floor(b64.length * 3 / 4);
  if (approxDecodedBytes > MAX_DATA_FONT_BYTES) {
    throw new Error(
      `Font data payload exceeds ${MAX_DATA_FONT_BYTES} byte limit`
    );
  }
  const data = Buffer.from(b64, "base64");
  if (data.length === 0) throw new Error("Decoded font buffer is empty");
  if (data.length > MAX_DATA_FONT_BYTES) {
    throw new Error(
      `Font data payload exceeds ${MAX_DATA_FONT_BYTES} byte limit`
    );
  }
  const format = detectFontFormat(data);
  if (format === "unknown") {
    throw new Error(
      "Decoded font buffer is not a recognized font (expected TTF/OTF/WOFF/WOFF2)"
    );
  }
  return {
    data,
    weight: input.weight ?? 400,
    italic: input.italic ?? false,
    format
  };
}

// src/fonts/sources/google-fetcher.ts
var TTF_UA = "Mozilla/4.0";
async function fetchWithTimeout(url, opts) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 5e3);
  try {
    const f = opts.fetcher ?? fetch;
    return await f(url, { headers: opts.headers, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}
function buildCssUrl(family, weights, italics) {
  const famPart = encodeURIComponent(family).replace(/%20/g, "+");
  const sortedWeights = [...weights].sort((a, b) => a - b);
  if (italics) {
    const axis = sortedWeights.flatMap((w) => [`0,${w}`, `1,${w}`]).join(";");
    return `https://fonts.googleapis.com/css2?family=${famPart}:ital,wght@${axis}&display=swap`;
  }
  const wghtPart = sortedWeights.join(";");
  return `https://fonts.googleapis.com/css2?family=${famPart}:wght@${wghtPart}&display=swap`;
}
function parseCssFaces(css) {
  const out = [];
  const faceRe = /@font-face\s*\{([^}]*)\}/g;
  let m;
  while ((m = faceRe.exec(css)) !== null) {
    const block = m[1];
    const urlM = block.match(
      /src:\s*url\((https:\/\/fonts\.gstatic\.com\/[^)]+\.ttf)\)/
    );
    if (!urlM) continue;
    const weightM = block.match(/font-weight:\s*(\d+)/);
    const italicM = block.match(/font-style:\s*italic/);
    out.push({
      weight: weightM ? parseInt(weightM[1], 10) : 400,
      italic: Boolean(italicM),
      ttfUrl: urlM[1]
    });
  }
  return out;
}
function cacheKey(family, weight2, italic) {
  return `google|${family}|${weight2}|${italic ? "i" : "r"}`;
}
async function fetchGoogleFontSources(opts) {
  const weights = opts.weights?.length ? opts.weights : [400, 700];
  const italics = opts.italics ?? false;
  const warnings = [];
  const sources = [];
  const wanted = [];
  for (const w of weights) {
    wanted.push({ weight: w, italic: false });
    if (italics) wanted.push({ weight: w, italic: true });
  }
  const pending = [];
  for (const w of wanted) {
    const key = cacheKey(opts.family, w.weight, w.italic);
    const mem = opts.memoryCache?.get(key);
    if (mem) {
      sources.push({
        data: mem,
        weight: w.weight,
        italic: w.italic,
        format: detectFontFormat(mem)
      });
      continue;
    }
    const disk = await opts.diskCache?.get(key);
    if (disk) {
      opts.memoryCache?.set(key, disk);
      sources.push({
        data: disk,
        weight: w.weight,
        italic: w.italic,
        format: detectFontFormat(disk)
      });
      continue;
    }
    pending.push(w);
  }
  if (pending.length === 0) {
    return { sources, warnings };
  }
  const needItalics = pending.some((p) => p.italic);
  const cssUrl = buildCssUrl(
    opts.family,
    Array.from(new Set(pending.map((p) => p.weight))),
    needItalics
  );
  let faces;
  try {
    const cssRes = await fetchWithTimeout(cssUrl, {
      headers: { "User-Agent": TTF_UA },
      timeoutMs: opts.fetchTimeoutMs,
      fetcher: opts.fetcher
    });
    if (!cssRes.ok) {
      warnings.push(
        `Google Fonts CSS fetch for "${opts.family}" returned ${cssRes.status}`
      );
      return { sources, warnings };
    }
    const css = await cssRes.text();
    faces = parseCssFaces(css);
  } catch (err) {
    warnings.push(
      `Google Fonts CSS fetch for "${opts.family}" failed: ${err.message}`
    );
    return { sources, warnings };
  }
  for (const need of pending) {
    const match = faces.find(
      (f) => f.weight === need.weight && f.italic === need.italic
    );
    if (!match) {
      warnings.push(
        `Google Fonts "${opts.family}" missing weight ${need.weight}${need.italic ? " italic" : ""}`
      );
      continue;
    }
    try {
      const res = await fetchWithTimeout(match.ttfUrl, {
        timeoutMs: opts.fetchTimeoutMs,
        fetcher: opts.fetcher
      });
      if (!res.ok) {
        warnings.push(
          `Google Fonts TTF fetch for "${opts.family}" ${need.weight} returned ${res.status}`
        );
        continue;
      }
      const ab = await res.arrayBuffer();
      const buf = Buffer.from(ab);
      const key = cacheKey(opts.family, need.weight, need.italic);
      opts.memoryCache?.set(key, buf);
      await opts.diskCache?.set(key, buf);
      sources.push({
        data: buf,
        weight: need.weight,
        italic: need.italic,
        format: detectFontFormat(buf)
      });
    } catch (err) {
      warnings.push(
        `Google Fonts TTF fetch for "${opts.family}" ${need.weight} failed: ${err.message}`
      );
    }
  }
  return { sources, warnings };
}

// src/fonts/sources/url-fetcher.ts
function cacheKey2(url, weight2, italic) {
  return `url|${url}|${weight2}|${italic ? "i" : "r"}`;
}
async function fetchUrlFontSource(opts) {
  if (!isAllowedFontUrl(opts.url)) {
    return {
      warnings: [
        `URL font fetch rejected (host not in allowlist or non-HTTPS): ${opts.url}`
      ]
    };
  }
  const key = cacheKey2(opts.url, opts.weight, opts.italic);
  const mem = opts.memoryCache?.get(key);
  if (mem) {
    return {
      source: {
        data: mem,
        weight: opts.weight,
        italic: opts.italic,
        format: detectFontFormat(mem)
      },
      warnings: []
    };
  }
  const disk = await opts.diskCache?.get(key);
  if (disk) {
    opts.memoryCache?.set(key, disk);
    return {
      source: {
        data: disk,
        weight: opts.weight,
        italic: opts.italic,
        format: detectFontFormat(disk)
      },
      warnings: []
    };
  }
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.fetchTimeoutMs ?? 1e4);
  try {
    const f = opts.fetcher ?? fetch;
    let res = await f(opts.url, { signal: ctrl.signal, redirect: "manual" });
    let hops = 0;
    while (res.status >= 300 && res.status < 400 && res.status !== 304) {
      const next = res.headers.get("location");
      if (!next) {
        return {
          warnings: [
            `URL font fetch "${opts.url}" ${res.status} with no Location header`
          ]
        };
      }
      const resolved = new URL(next, opts.url).toString();
      if (!isAllowedFontUrl(resolved)) {
        return {
          warnings: [
            `URL font fetch "${opts.url}" redirected to disallowed host: ${resolved}`
          ]
        };
      }
      if (++hops > 3) {
        return {
          warnings: [`URL font fetch "${opts.url}" too many redirects`]
        };
      }
      res = await f(resolved, { signal: ctrl.signal, redirect: "manual" });
    }
    if (!res.ok) {
      return {
        warnings: [`URL font fetch "${opts.url}" returned ${res.status}`]
      };
    }
    const ab = await res.arrayBuffer();
    const raw = Buffer.from(ab);
    const format = detectFontFormat(raw);
    if (format === "unknown" || raw.length < 512) {
      return {
        warnings: [
          `URL font fetch "${opts.url}" returned ${raw.length} bytes of ${format} \u2014 not a TTF/OTF. Skipping.`
        ]
      };
    }
    const buf = raw;
    opts.memoryCache?.set(key, buf);
    await opts.diskCache?.set(key, buf);
    return {
      source: {
        data: buf,
        weight: opts.weight,
        italic: opts.italic,
        format
      },
      warnings: []
    };
  } catch (err) {
    return {
      warnings: [
        `URL font fetch "${opts.url}" failed: ${err.message}`
      ]
    };
  } finally {
    clearTimeout(timer);
  }
}

// src/fonts/sources/ttf-structure.ts
var SFNT_VERSIONS = /* @__PURE__ */ new Set([
  65536,
  // TrueType outlines
  1330926671,
  // 'OTTO' — CFF outlines
  1953658213,
  // 'true'
  1954115633
  // 'typ1'
]);
var HEADER_SIZE = 12;
var TABLE_RECORD_SIZE = 16;
function validateFontStructure(ttf, weight2, italic, familyLabel) {
  const face = `Font "${familyLabel}" weight ${weight2}${italic ? " italic" : ""}`;
  const unreadable = (reason) => ({
    code: "FONT_UNREADABLE",
    message: `${face}: ${reason} The face will not resolve; text referencing it renders in a fallback.`
  });
  if (ttf.length < HEADER_SIZE) {
    return unreadable(
      `${ttf.length} bytes is shorter than an sfnt header \u2014 the download is truncated or empty.`
    );
  }
  const version = ttf.readUInt32BE(0);
  if (!SFNT_VERSIONS.has(version)) {
    return unreadable(
      `sfnt version 0x${version.toString(16).padStart(8, "0")} is neither TrueType nor OpenType.`
    );
  }
  const numTables = ttf.readUInt16BE(4);
  if (numTables === 0) return unreadable("its table directory is empty.");
  const directoryEnd = HEADER_SIZE + numTables * TABLE_RECORD_SIZE;
  if (directoryEnd > ttf.length) {
    return unreadable(
      `its directory claims ${numTables} tables (${directoryEnd} bytes) but the file is ${ttf.length} bytes \u2014 truncated.`
    );
  }
  const tags = /* @__PURE__ */ new Set();
  for (let i = 0; i < numTables; i += 1) {
    const ro = HEADER_SIZE + i * TABLE_RECORD_SIZE;
    const tag = ttf.toString("ascii", ro, ro + 4);
    const offset = ttf.readUInt32BE(ro + 8);
    const length = ttf.readUInt32BE(ro + 12);
    if (offset + length > ttf.length) {
      return unreadable(
        `its "${tag}" table runs to byte ${offset + length} but the file is ${ttf.length} bytes \u2014 truncated.`
      );
    }
    tags.add(tag);
  }
  for (const required of ["name", "head"]) {
    if (!tags.has(required)) {
      return unreadable(`it has no "${required}" table.`);
    }
  }
  const hasTrueTypeOutlines = tags.has("glyf") && tags.has("loca");
  const hasCffOutlines = tags.has("CFF ") || tags.has("CFF2");
  if (!hasTrueTypeOutlines && !hasCffOutlines) {
    return unreadable(
      'it carries no glyph outlines (neither "glyf" + "loca" nor "CFF ").'
    );
  }
  if (readNameRecords(ttf).length === 0) {
    return unreadable('its "name" table carries no readable records.');
  }
  return null;
}

// src/fonts/cache/memory-cache.ts
var FontMemoryCache = class {
  store = /* @__PURE__ */ new Map();
  bytes = 0;
  maxBytes;
  constructor(opts = {}) {
    this.maxBytes = opts.maxBytes ?? 20 * 1024 * 1024;
  }
  get(key) {
    const v = this.store.get(key);
    if (!v) return void 0;
    this.store.delete(key);
    this.store.set(key, v);
    return v;
  }
  set(key, value) {
    const existing = this.store.get(key);
    if (existing) this.bytes -= existing.byteLength;
    this.store.set(key, value);
    this.bytes += value.byteLength;
    while (this.bytes > this.maxBytes && this.store.size > 0) {
      const oldest = this.store.keys().next().value;
      if (!oldest) break;
      const removed = this.store.get(oldest);
      this.store.delete(oldest);
      if (removed) this.bytes -= removed.byteLength;
    }
  }
  size() {
    return this.store.size;
  }
};

// src/fonts/registry.ts
function stampResolvedFamily(source, family) {
  if (source.format !== "ttf" && source.format !== "otf") return source;
  const declared = readFontFamilyNames(source.data);
  if (declared.length === 0 || declared.includes(family)) return source;
  const subfamily = standardSubfamilyNames(
    source.weight,
    source.italic
  )?.legacy;
  return {
    ...source,
    data: rewriteFontFamilyName(source.data, family, subfamily)
  };
}
var FontRegistry = class {
  index;
  cache;
  opts;
  memoryCache;
  diskCache;
  fileLoader;
  variableLoader;
  constructor(input = {}) {
    this.opts = input.opts ?? {};
    this.index = /* @__PURE__ */ new Map();
    this.cache = /* @__PURE__ */ new Map();
    this.memoryCache = new FontMemoryCache();
    this.diskCache = input.diskCache;
    this.fileLoader = input.fileLoader;
    this.variableLoader = input.variableLoader;
    for (const e of this.opts.extraEntries ?? []) this.addEntry(e);
  }
  addEntry(entry) {
    this.index.set(entry.family.toLowerCase(), entry);
    this.index.set(entry.id.toLowerCase(), entry);
  }
  /** Resolve every referenced name in one pass. Order preserved. */
  async resolveMany(names) {
    const out = [];
    for (const n of names) out.push(await this.resolve(n));
    return out;
  }
  async resolve(name) {
    const key = name.toLowerCase();
    const cached = this.cache.get(key);
    if (cached) return cached;
    const entry = this.index.get(key);
    let result;
    if (entry) {
      result = await this.materializeEntry(entry);
    } else if (isSafeFont(name)) {
      result = { family: name, sources: [], warnings: [] };
    } else {
      result = {
        family: name,
        sources: [],
        warnings: [
          `Font "${name}" is not registered and not in SAFE_FONTS; will rely on host fallback.`
        ]
      };
    }
    this.cache.set(key, result);
    return result;
  }
  async materializeEntry(entry) {
    const sources = [];
    const warnings = [];
    for (const source of entry.sources) {
      try {
        const materialized = await this.materializeSource(source, warnings);
        for (const raw of materialized) {
          const sfnt = raw.format === "ttf" || raw.format === "otf";
          const broken = sfnt ? validateFontStructure(
            raw.data,
            raw.weight,
            raw.italic,
            entry.family
          ) : null;
          if (broken) {
            warnings.push(`[${broken.code}] ${broken.message}`);
            sources.push(raw);
            continue;
          }
          const s = stampResolvedFamily(raw, entry.family);
          if (sfnt) {
            for (const d of validateFontMetadata(
              s.data,
              s.weight,
              s.italic,
              entry.family
            )) {
              warnings.push(`[FONT_METADATA_DEFECT:${d.code}] ${d.message}`);
            }
          }
          sources.push(s);
        }
      } catch (err) {
        warnings.push(
          `Font "${entry.family}" source (${source.kind}) failed: ${err.message}`
        );
      }
    }
    return {
      family: entry.family,
      sources,
      warnings
    };
  }
  async materializeSource(source, warnings) {
    switch (source.kind) {
      case "safe":
        return [];
      case "file": {
        if (!this.fileLoader) {
          warnings.push(
            `kind:"file" source for "${source.path}" requires a fileLoader (Node-only); skipping.`
          );
          return [];
        }
        return [
          await this.fileLoader({
            path: source.path,
            weight: source.weight,
            italic: source.italic,
            baseDir: this.opts.baseDir
          })
        ];
      }
      case "data":
        return [
          loadDataFontSource({
            data: source.data,
            weight: source.weight,
            italic: source.italic
          })
        ];
      case "google": {
        const gf = this.opts.googleFonts;
        if (gf?.enabled === false) {
          warnings.push(
            `Google Fonts fetch disabled \u2014 skipping "${source.family}".`
          );
          return [];
        }
        const { sources: fetched, warnings: fetchWarnings } = await fetchGoogleFontSources({
          family: source.family,
          weights: source.weights ?? [400, 700],
          italics: source.italics ?? false,
          memoryCache: this.memoryCache,
          diskCache: this.diskCache,
          fetchTimeoutMs: gf?.fetchTimeoutMs
        });
        warnings.push(...fetchWarnings);
        return fetched;
      }
      case "url": {
        const gf = this.opts.googleFonts;
        const { source: fetched, warnings: fetchWarnings } = await fetchUrlFontSource({
          url: source.url,
          weight: source.weight ?? 400,
          italic: source.italic ?? false,
          memoryCache: this.memoryCache,
          diskCache: this.diskCache,
          fetchTimeoutMs: gf?.fetchTimeoutMs
        });
        if (fetchWarnings) warnings.push(...fetchWarnings);
        return fetched ? [fetched] : [];
      }
      case "variable": {
        if (!this.variableLoader) {
          warnings.push(
            `kind:"variable" source for "${source.url}" requires a variableLoader (Node-only); skipping.`
          );
          return [];
        }
        const gf = this.opts.googleFonts;
        const { source: fetched, warnings: fetchWarnings } = await this.variableLoader({
          url: source.url,
          weight: source.weight,
          italic: source.italic ?? false,
          axes: source.axes,
          memoryCache: this.memoryCache,
          diskCache: this.diskCache,
          fetchTimeoutMs: gf?.fetchTimeoutMs
        });
        if (fetchWarnings) warnings.push(...fetchWarnings);
        return fetched ? [fetched] : [];
      }
      default:
        throw new Error(
          `Unknown font source kind: ${source.kind}`
        );
    }
  }
};

// src/fonts/catalog/popular-google.ts
var POPULAR_GOOGLE_FONTS = [
  // Sans-serif
  {
    family: "Inter",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: false
  },
  {
    family: "Roboto",
    category: "sans",
    weights: [100, 300, 400, 500, 700, 900],
    hasItalic: true
  },
  {
    family: "Open Sans",
    category: "sans",
    weights: [300, 400, 500, 600, 700, 800],
    hasItalic: true
  },
  {
    family: "Lato",
    category: "sans",
    weights: [100, 300, 400, 700, 900],
    hasItalic: true
  },
  {
    family: "Montserrat",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Poppins",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Work Sans",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Nunito",
    category: "sans",
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "DM Sans",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Rubik",
    category: "sans",
    weights: [300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Source Sans 3",
    category: "sans",
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Manrope",
    category: "sans",
    weights: [200, 300, 400, 500, 600, 700, 800],
    hasItalic: false
  },
  {
    family: "Plus Jakarta Sans",
    category: "sans",
    weights: [200, 300, 400, 500, 600, 700, 800],
    hasItalic: true
  },
  {
    family: "IBM Plex Sans",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700],
    hasItalic: true
  },
  {
    family: "Archivo",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Geist",
    category: "sans",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Space Grotesk",
    category: "sans",
    weights: [300, 400, 500, 600, 700],
    hasItalic: false
  },
  // Serif
  {
    family: "Playfair Display",
    category: "serif",
    weights: [400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Merriweather",
    category: "serif",
    weights: [300, 400, 700, 900],
    hasItalic: true
  },
  {
    family: "Lora",
    category: "serif",
    weights: [400, 500, 600, 700],
    hasItalic: true
  },
  {
    family: "Source Serif 4",
    category: "serif",
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "DM Serif Display",
    category: "serif",
    weights: [400],
    hasItalic: true
  },
  {
    family: "Crimson Pro",
    category: "serif",
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Cormorant Garamond",
    category: "serif",
    weights: [300, 400, 500, 600, 700],
    hasItalic: true
  },
  // Monospace
  {
    family: "JetBrains Mono",
    category: "mono",
    weights: [100, 200, 300, 400, 500, 600, 700, 800],
    hasItalic: true
  },
  {
    family: "Fira Code",
    category: "mono",
    weights: [300, 400, 500, 600, 700],
    hasItalic: false
  },
  {
    family: "IBM Plex Mono",
    category: "mono",
    weights: [100, 200, 300, 400, 500, 600, 700],
    hasItalic: true
  },
  {
    family: "Source Code Pro",
    category: "mono",
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  {
    family: "Space Mono",
    category: "mono",
    weights: [400, 700],
    hasItalic: true
  },
  {
    family: "Geist Mono",
    category: "mono",
    weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
    hasItalic: true
  },
  // Display
  {
    family: "Bebas Neue",
    category: "display",
    weights: [400],
    hasItalic: false
  },
  {
    family: "Abril Fatface",
    category: "display",
    weights: [400],
    hasItalic: false
  },
  {
    family: "Archivo Black",
    category: "display",
    weights: [400],
    hasItalic: false
  },
  {
    family: "Oswald",
    category: "display",
    weights: [200, 300, 400, 500, 600, 700],
    hasItalic: false
  },
  // Handwriting
  {
    family: "Caveat",
    category: "handwriting",
    weights: [400, 500, 600, 700],
    hasItalic: false
  },
  {
    family: "Pacifico",
    category: "handwriting",
    weights: [400],
    hasItalic: false
  }
];

// src/fonts/catalog/upstream-overrides.ts
var INTER_VARIABLE_URL = "https://cdn.jsdelivr.net/gh/rsms/inter@v4.1/docs/font-files/InterVariable.ttf";
var INTER_VARIABLE_ITALIC_URL = "https://cdn.jsdelivr.net/gh/rsms/inter@v4.1/docs/font-files/InterVariable-Italic.woff2";
function interVariants() {
  const weights = [100, 200, 300, 400, 500, 600, 700, 800, 900];
  const upright = weights.map((weight2) => ({
    kind: "variable",
    url: INTER_VARIABLE_URL,
    weight: weight2,
    italic: false
  }));
  const italic = weights.map((weight2) => ({
    kind: "variable",
    url: INTER_VARIABLE_ITALIC_URL,
    weight: weight2,
    italic: true
  }));
  return [...upright, ...italic];
}
var UPSTREAM_OVERRIDES = {
  inter: {
    reason: "Google's static Inter Thin/ExtraLight both carry usWeightClass=250 and near-identical glyph outlines (xAvgCharWidth differs by 1.8%); instancing the upstream variable font per weight produces properly distinct statics.",
    variants: interVariants()
  }
};
function getUpstreamOverride(family) {
  return UPSTREAM_OVERRIDES[family.toLowerCase()];
}

// src/fonts/substitute.ts
function applyFontSubstitution(doc, mapping) {
  const seen = /* @__PURE__ */ new Map();
  const rewritten = rewrite(doc, mapping, seen);
  const substitutions = [];
  for (const [from, to] of seen) substitutions.push({ from, to });
  return { doc: rewritten, substitutions };
}
function rewrite(node, mapping, seen, parentKey) {
  if (node == null) return node;
  if (typeof node === "string") {
    if (parentKey && (FONT_NAME_KEYS.has(parentKey) || THEME_FONT_KEYS.has(parentKey))) {
      return maybeSwap(node, mapping, seen);
    }
    return node;
  }
  if (Array.isArray(node)) {
    return node.map((item) => rewrite(item, mapping, seen, parentKey));
  }
  if (typeof node === "object") {
    const out = {};
    for (const [k, v] of Object.entries(node)) {
      if (FONT_DECLARATION_KEYS.has(k)) {
        out[k] = v;
        continue;
      }
      if (parentKey === "theme" && k === "fonts" && v && typeof v === "object") {
        const nextFonts = {};
        for (const [fk, fv] of Object.entries(v)) {
          if (typeof fv === "string") {
            nextFonts[fk] = maybeSwap(fv, mapping, seen);
          } else if (fv && typeof fv === "object") {
            const fam = fv.family;
            nextFonts[fk] = typeof fam === "string" ? { ...fv, family: maybeSwap(fam, mapping, seen) } : rewrite(fv, mapping, seen, fk);
          } else {
            nextFonts[fk] = fv;
          }
        }
        out[k] = nextFonts;
        continue;
      }
      out[k] = rewrite(v, mapping, seen, k);
    }
    return out;
  }
  return node;
}
function maybeSwap(name, mapping, seen) {
  const trimmed = name.trim();
  if (trimmed.length === 0) return name;
  if (isSafeFont(trimmed)) return name;
  const lowered = trimmed.toLowerCase();
  for (const [from, to] of Object.entries(mapping)) {
    if (from.toLowerCase() === lowered) {
      seen.set(trimmed, to);
      return to;
    }
  }
  return name;
}
var EXPLICIT_OVERRIDES = {
  Inter: "Calibri",
  Roboto: "Calibri",
  "Open Sans": "Calibri",
  Lato: "Calibri",
  "Source Sans 3": "Calibri",
  "Source Sans Pro": "Calibri",
  "IBM Plex Sans": "Calibri",
  Archivo: "Calibri",
  Geist: "Calibri",
  "Space Grotesk": "Calibri",
  "Work Sans": "Calibri",
  Manrope: "Calibri",
  Nunito: "Calibri",
  "Nunito Sans": "Calibri",
  Poppins: "Calibri",
  Montserrat: "Calibri",
  "Playfair Display": "Georgia",
  Merriweather: "Georgia",
  "Source Serif 4": "Cambria",
  "Source Serif Pro": "Cambria",
  "IBM Plex Serif": "Cambria",
  "Crimson Pro": "Cambria",
  Lora: "Georgia",
  "PT Serif": "Georgia",
  "Cormorant Garamond": "Cambria",
  "JetBrains Mono": "Consolas",
  "Fira Code": "Consolas",
  "IBM Plex Mono": "Consolas",
  "Source Code Pro": "Consolas",
  "Roboto Mono": "Consolas",
  "Geist Mono": "Consolas"
};
var CATEGORY_FALLBACK = {
  sans: "Calibri",
  serif: "Georgia",
  mono: "Consolas",
  display: "Georgia",
  handwriting: "Segoe UI"
};
function defaultSubstituteFor(family) {
  const trimmed = family.trim();
  for (const [from, to] of Object.entries(EXPLICIT_OVERRIDES)) {
    if (from.toLowerCase() === trimmed.toLowerCase()) return to;
  }
  const catalog = POPULAR_GOOGLE_FONTS.find(
    (f) => f.family.toLowerCase() === trimmed.toLowerCase()
  );
  if (catalog) {
    const cat = CATEGORY_FALLBACK[catalog.category];
    if (cat) return cat;
  }
  return "Calibri";
}
function buildDefaultSubstitutionMap(referencedNames) {
  const out = {};
  for (const raw of referencedNames) {
    const name = raw.trim();
    if (name.length === 0) continue;
    if (isSafeFont(name)) continue;
    if (out[name]) continue;
    out[name] = defaultSubstituteFor(name);
  }
  return out;
}
function applyExportMode(input) {
  const mode = input.fonts?.mode ?? "custom";
  if (mode === "embed") {
    return { doc: input.doc, theme: input.theme, warnings: [] };
  }
  if (mode === "custom") {
    if (!input.fonts) {
      return { doc: input.doc, theme: input.theme, warnings: [] };
    }
    const referenced2 = /* @__PURE__ */ new Set([
      ...collectFontNames(input.doc),
      ...collectFontNames(input.theme)
    ]);
    const nonSafe = [...referenced2].filter((name) => !isSafeFont(name.trim()));
    const warnings2 = nonSafe.length > 0 ? [
      {
        code: "FONT_MODE_CUSTOM",
        message: `Export mode "custom": non-safe font references (${nonSafe.join(", ")}) kept as-is. Recipients need these fonts installed locally; Word falls back to a generic substitute otherwise.`
      }
    ] : [];
    return {
      doc: input.doc,
      theme: input.theme,
      warnings: warnings2
    };
  }
  const referenced = /* @__PURE__ */ new Set([
    ...collectFontNames(input.doc),
    ...collectFontNames(input.theme)
  ]);
  const defaults = buildDefaultSubstitutionMap(referenced);
  const mapping = {
    ...defaults,
    ...input.fonts?.substitution ?? {}
  };
  const docRewrite = applyFontSubstitution(input.doc, mapping);
  const themeRewrite = applyFontSubstitution(input.theme, mapping);
  const combined = /* @__PURE__ */ new Map();
  for (const s of docRewrite.substitutions) combined.set(s.from, s.to);
  for (const s of themeRewrite.substitutions) combined.set(s.from, s.to);
  const warnings = [];
  if (combined.size > 0) {
    const list = [...combined].map(([from, to]) => `${from} \u2192 ${to}`).join(", ");
    warnings.push({
      code: "FONT_MODE_SUBSTITUTED",
      message: `Export mode "substitute": rewrote non-safe families to safe equivalents \u2014 ${list}. No fonts embedded; document renders identically on every machine.`
    });
  }
  return {
    doc: docRewrite.doc,
    theme: themeRewrite.doc,
    warnings
  };
}

// src/theme/chart-palette.ts
var DEFAULT_CHART_THEME_COLORS = [
  "primary",
  "secondary",
  "accent",
  "accent4",
  "accent5",
  "accent6"
];

// src/theme/chart-typography.ts
var POINTS_PER_PIXEL_96DPI = 0.75;
function chartPointsPerPixel(chartWidthPx, placedWidthPt) {
  if (!Number.isFinite(chartWidthPx) || chartWidthPx <= 0 || placedWidthPt === void 0 || !Number.isFinite(placedWidthPt) || placedWidthPt <= 0) {
    return POINTS_PER_PIXEL_96DPI;
  }
  return placedWidthPt / chartWidthPx;
}
var SERIF_FAMILIES = /* @__PURE__ */ new Set(["georgia", "times new roman", "cambria"]);
var MONO_FAMILIES = /* @__PURE__ */ new Set(["consolas", "courier new", "menlo", "monaco"]);
function cssFontFamily(family, category) {
  const generic = category === "serif" ? "serif" : category === "mono" ? "monospace" : category === "handwriting" ? "cursive" : category === void 0 && SERIF_FAMILIES.has(family.toLowerCase()) ? "serif" : category === void 0 && MONO_FAMILIES.has(family.toLowerCase()) ? "monospace" : "sans-serif";
  return `"${family.replace(/["\\]/g, "\\$&")}", ${generic}`;
}
function chartFamilyResolver(theme) {
  const categories = new Map(
    themeFontRegistry(theme).map((entry) => [
      entry.family.toLowerCase(),
      entry.category
    ])
  );
  return (family) => cssFontFamily(family, categories.get(family.toLowerCase()));
}
function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function fill(authored, defaults) {
  if (authored !== void 0 && !isPlainObject(authored)) return authored;
  const base = isPlainObject(authored) ? { ...authored } : {};
  for (const [key, value] of Object.entries(defaults)) {
    if (value === void 0) continue;
    const current = base[key];
    if (current === void 0) {
      base[key] = isPlainObject(value) ? fill(void 0, value) : value;
    } else if (isPlainObject(current) && isPlainObject(value)) {
      base[key] = fill(current, value);
    }
  }
  return base;
}
function fillChartOptions(authored, defaults) {
  return fill(authored, defaults);
}
function fillAxis(authored, defaults) {
  if (Array.isArray(authored)) {
    return authored.map((axis) => fill(axis, defaults));
  }
  return fill(authored, defaults);
}
function weight(value) {
  return value === void 0 ? void 0 : String(value);
}
function withChartTypography(options, typography, ptPerPx) {
  const px = (points) => `${Math.round(points / ptPerPx * 10) / 10}px`;
  const labelPx = px(typography.labelPt);
  const sourcePx = px(typography.sourcePt);
  const mutedText = { fontSize: labelPx, color: typography.mutedColor };
  const mutedSource = { fontSize: sourcePx, color: typography.mutedColor };
  const labelText = {
    fontSize: labelPx,
    color: typography.textColor,
    fontWeight: weight(typography.labelWeight)
  };
  const axis = { labels: { style: mutedText }, title: { style: mutedText } };
  return {
    ...options,
    chart: fill(options.chart, {
      style: { fontFamily: typography.bodyFamily }
    }),
    title: fill(options.title, {
      style: {
        fontFamily: typography.headingFamily,
        fontSize: px(typography.titlePt),
        fontWeight: weight(typography.titleWeight),
        color: typography.textColor
      }
    }),
    subtitle: fill(options.subtitle, { style: mutedText }),
    caption: fill(options.caption, { style: mutedSource }),
    xAxis: fillAxis(options.xAxis, axis),
    yAxis: fillAxis(options.yAxis, axis),
    legend: fill(options.legend, { itemStyle: labelText }),
    plotOptions: fill(options.plotOptions, {
      series: { dataLabels: { style: labelText } }
    }),
    // The library credit is a link to highcharts.com drawn into the plot
    // area; a client document never carries it unless the author asks. An
    // authored `enabled` — true included — wins, as every other key does.
    credits: fill(options.credits, { enabled: false, style: mutedSource })
  };
}
var FONT_FORMATS = {
  ttf: { mime: "font/ttf", format: "truetype" },
  otf: { mime: "font/otf", format: "opentype" },
  woff: { mime: "font/woff", format: "woff" },
  woff2: { mime: "font/woff2", format: "woff2" }
};
function chartFontFaceCss(faces, families) {
  const wanted = new Set(families.map((family) => family.toLowerCase()));
  return faces.filter((face) => wanted.has(face.family.toLowerCase())).map((face) => {
    const { mime, format } = FONT_FORMATS[face.format ?? "ttf"];
    return `@font-face{font-family:"${face.family.replace(/["\\]/g, "\\$&")}";font-weight:${face.weight};font-style:${face.italic ? "italic" : "normal"};src:url(data:${mime};base64,${face.data}) format("${format}")}`;
  }).join("\n");
}
function withChartFontFaceCss(props, faces, families) {
  const css = chartFontFaceCss(faces, families);
  if (!css) return props;
  const authored = props.resources?.css;
  return {
    ...props,
    resources: {
      ...props.resources,
      css: authored ? `${css}
${authored}` : css
    }
  };
}

// src/theme/chart-presets.ts
function withChartPresets(options, presets) {
  if (!presets?.options) return options;
  return fillChartOptions(options, presets.options);
}

// src/schemas/highcharts-defaults.ts
import { Type } from "@sinclair/typebox";
var HighchartsThemeDefaultsSchema = Type.Object(
  {
    options: Type.Optional(
      Type.Record(Type.String(), Type.Unknown(), {
        description: "Highcharts options written beneath every chart\u2019s own options, key by key, as `Highcharts.setOptions` would; an authored value always wins. Any Highcharts option goes; per series type, use `plotOptions.<type>`."
      })
    )
  },
  {
    additionalProperties: false,
    description: "The theme\u2019s Highcharts options, the equivalent of `Highcharts.setOptions`: `options` is written beneath every chart\u2019s own options; the chart\u2019s own values win."
  }
);

// src/blocks/schema.ts
import { Type as Type2 } from "@sinclair/typebox";
var BLOCK_SLOT_ROLES = [
  "actionTitle",
  "takeaway",
  "source",
  "tracker",
  "footer",
  "logo"
];
var BlockSlotSchema = Type2.Recursive(
  (Self) => Type2.Object(
    {
      type: Type2.Union(
        [
          "string",
          "number",
          "integer",
          "boolean",
          "object",
          "array",
          "component"
        ].map((v) => Type2.Literal(v)),
        {
          description: "Content type accepted by this slot. Use component for a document component or registered plugin."
        }
      ),
      description: Type2.Optional(
        Type2.String({
          description: "Explain this slot\u2019s content and purpose to authors."
        })
      ),
      required: Type2.Optional(
        Type2.Boolean({
          description: "Require a value when no default is provided. Defaults to false."
        })
      ),
      default: Type2.Optional(
        Type2.Unknown({
          description: "Value used when the caller omits this slot. Must satisfy the slot\u2019s type and constraints."
        })
      ),
      enum: Type2.Optional(
        Type2.Array(
          Type2.Union([Type2.String(), Type2.Number(), Type2.Boolean()]),
          {
            minItems: 1,
            description: "Allowed scalar values for this slot."
          }
        )
      ),
      minItems: Type2.Optional(
        Type2.Integer({
          minimum: 0,
          description: "Minimum number of array entries, inclusive."
        })
      ),
      maxItems: Type2.Optional(
        Type2.Integer({
          minimum: 0,
          description: "Maximum number of array entries, inclusive."
        })
      ),
      minLength: Type2.Optional(
        Type2.Integer({
          minimum: 0,
          description: "Minimum string length in characters, inclusive."
        })
      ),
      maxLength: Type2.Optional(
        Type2.Integer({
          minimum: 0,
          description: "Maximum string length in characters, inclusive."
        })
      ),
      minimum: Type2.Optional(
        Type2.Number({ description: "Minimum numeric value, inclusive." })
      ),
      maximum: Type2.Optional(
        Type2.Number({ description: "Maximum numeric value, inclusive." })
      ),
      maxWords: Type2.Optional(
        Type2.Integer({
          minimum: 1,
          description: "Maximum whitespace-separated word count. Exceeding it fails validation."
        })
      ),
      oneLine: Type2.Optional(
        Type2.Boolean({
          description: "Reject newline characters in string values. Does not prevent visual line wrapping."
        })
      ),
      items: Type2.Optional({
        ...Self,
        description: "Slot type and constraints for each array entry."
      }),
      properties: Type2.Optional(
        Type2.Record(Type2.String(), Self, {
          description: "Named child slots accepted by an object slot. Undeclared properties are rejected."
        })
      ),
      role: Type2.Optional(
        Type2.Union(
          BLOCK_SLOT_ROLES.map((role) => Type2.Literal(role)),
          {
            description: "Content role for quality profiles: actionTitle, takeaway, source, tracker, footer or logo. A profile may require or measure it; the theme only styles it. Tracker, footer, source and logo are chrome, which a deck may draw in the margin band."
          }
        )
      )
    },
    { additionalProperties: false }
  ),
  // Named so the export hoists it under a stable definition rather than a
  // TypeBox ordinal that shifts with what the process built before it.
  { $id: "BlockSlot" }
);
var JsonBlockDefinitionSchema = Type2.Unsafe(
  Type2.Object(
    {
      description: Type2.Optional(
        Type2.String({
          description: "Describe what this reusable block renders and when to use it."
        })
      ),
      slots: Type2.Record(Type2.String(), BlockSlotSchema, {
        description: "Named inputs and their types, defaults and constraints. Use an empty object for a block with no inputs."
      }),
      body: Type2.Array(Type2.Unknown(), {
        description: "Components and binding directives expanded in order when this block is invoked."
      }),
      section: Type2.Optional(
        Type2.Object(
          {
            tracker: Type2.Optional(
              Type2.Unknown({
                description: "Section tracker value or binding, available to headers and footers through $context at /section/tracker."
              })
            ),
            header: Type2.Optional(
              Type2.Array(Type2.Unknown(), {
                description: "Header component templates. Explicit header settings on the section take precedence."
              })
            ),
            footer: Type2.Optional(
              Type2.Array(Type2.Unknown(), {
                description: "Footer component templates. Explicit footer settings on the section take precedence."
              })
            ),
            pageBreak: Type2.Optional(
              Type2.Boolean({
                description: "Start the containing section on a new page. An explicit section pageBreak setting takes precedence."
              })
            ),
            scope: Type2.Optional(
              Type2.Union([Type2.Literal("section"), Type2.Literal("following")], {
                description: "Apply header/footer templates to this section only, or inherit them in following sections. Defaults to section."
              })
            )
          },
          {
            additionalProperties: false,
            description: "DOCX section tracker, header/footer templates and page-break behavior. Place this block at the section boundary."
          }
        )
      ),
      slide: Type2.Optional(
        Type2.Object(
          {
            background: Type2.Optional(
              Type2.Unknown({
                description: "Slide background (color, gradient or image) or a binding. A background the slide states itself takes precedence."
              })
            ),
            grid: Type2.Optional(
              Type2.Unknown({
                description: "Grid configuration merged over the presentation grid when resolving grid placements in this block\u2019s body."
              })
            ),
            notes: Type2.Optional(
              Type2.Unknown({
                description: "Speaker notes or a binding. Notes the slide states itself take precedence."
              })
            )
          },
          {
            additionalProperties: false,
            description: "PPTX slide background, grid and notes supplied by this block. Invoke the block as a direct child of a slide."
          }
        )
      )
    },
    { additionalProperties: false }
  )
);
var BlockDefinitionsSchema = Type2.Record(
  Type2.String({ pattern: "^[a-zA-Z][a-zA-Z0-9_-]*$" }),
  JsonBlockDefinitionSchema,
  {
    description: "Document-local JSON block definitions. Names are not built into the engine."
  }
);
var BlockInvocationPropsSchema = Type2.Object(
  {
    ref: Type2.String({
      minLength: 1,
      description: "Name in this document\u2019s props.blocks."
    }),
    slots: Type2.Optional(
      Type2.Record(Type2.String(), Type2.Unknown(), {
        description: "Input values keyed by the slot names declared in the referenced block definition."
      })
    )
  },
  { additionalProperties: false }
);
function blockSlotJsonSchema(slot) {
  const { oneLine, properties, items, role: _role, ...rest } = slot;
  delete rest.required;
  delete rest.maxWords;
  if (slot.type === "component") {
    return {
      type: "object",
      properties: { name: { type: "string" } },
      required: ["name"],
      description: slot.description
    };
  }
  return {
    ...rest,
    ...oneLine && { pattern: "^[^\\r\\n]*$" },
    ...items && { items: blockSlotJsonSchema(items) },
    ...properties && {
      properties: Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [
          key,
          blockSlotJsonSchema(value)
        ])
      ),
      required: Object.entries(properties).filter(([, value]) => value.required && value.default === void 0).map(([key]) => key),
      additionalProperties: false
    }
  };
}

// src/blocks/evaluator.ts
import { isScaffoldMarker } from "@json-to-office/quality";

// src/blocks/directives.ts
var BLOCK_DIRECTIVES = {
  $slot: { keys: ["$slot", "default", "props"], result: "dynamic" },
  $item: { keys: ["$item", "default", "props"], result: "dynamic" },
  $theme: { keys: ["$theme", "default"], result: "dynamic" },
  $context: { keys: ["$context", "default"], result: "dynamic" },
  $count: { keys: ["$count"], result: "number" },
  $if: { keys: ["$if", "then", "else"], result: "dynamic" },
  $each: { keys: ["$each", "template"], result: "array" },
  $join: { keys: ["$join", "separator", "keepEmpty"], result: "string" },
  $measure: { keys: ["$measure", "fraction", "unit"], result: "number" }
};
var BLOCK_OPERAND_ROOTS = [
  "$item",
  "$slot",
  "$context",
  "$theme"
];

// src/blocks/evaluator.ts
import { Value } from "@sinclair/typebox/value";
var BlockEvaluationError = class extends Error {
  constructor(issues) {
    super(issues.map((i) => `${i.path}: ${i.message}`).join("\n"));
    this.issues = issues;
    this.name = "BlockEvaluationError";
  }
};
var isBlockRecord = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
var blockPointerKey = (s) => s.replace(/~/g, "~0").replace(/\//g, "~1");
var own = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
function blockValueAt(root, path) {
  if (path === "") return root;
  if (!path.startsWith("/")) return void 0;
  let value = root;
  for (const part of path.slice(1).split("/")) {
    const key = part.replace(/~1/g, "/").replace(/~0/g, "~");
    if (!isBlockRecord(value) && !Array.isArray(value) || !own(value, key))
      return void 0;
    value = value[key];
  }
  return value;
}
function toAuthoredBlockPointer(map, pointer2, pluginOutputs = []) {
  let opaque;
  for (const prefix of pluginOutputs)
    if ((pointer2 === prefix || pointer2.startsWith(`${prefix}/`)) && (opaque === void 0 || prefix.length > opaque.length))
      opaque = prefix;
  if (opaque !== void 0) return toAuthoredBlockPointer(map, opaque);
  let best;
  for (const path of Object.keys(map)) {
    if ((pointer2 === path || pointer2.startsWith(`${path}/`)) && (best === void 0 || path.length > best.length))
      best = path;
  }
  return best === void 0 ? pointer2 : `${map[best]}${pointer2.slice(best.length)}`;
}
var BLOCK_SLOT_PLACEMENT_PROPS = [
  "x",
  "y",
  "w",
  "h",
  "position",
  "grid",
  "gridConfig",
  "direction",
  "gap",
  "weights",
  "alignment",
  "spacing"
];
var blockWordCount = (text) => text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
var present = (value) => value !== void 0 && value !== null && value !== "" && value !== false && (!Array.isArray(value) || value.length > 0);
var fail = (path, code, message) => {
  throw new BlockEvaluationError([{ path, code, message }]);
};
function resolveBlockSlot(slot, input, path, issues) {
  const value = input === void 0 && slot.default !== void 0 ? structuredClone(slot.default) : input;
  if (value === void 0) {
    if (slot.required)
      issues.push({
        path,
        code: "block_required_slot",
        message: "Required block slot is missing."
      });
    return void 0;
  }
  const validType = slot.type === "array" ? Array.isArray(value) : slot.type === "component" ? isBlockRecord(value) && typeof value.name === "string" : slot.type === "object" ? isBlockRecord(value) : slot.type === "integer" ? typeof value === "number" && Number.isInteger(value) : typeof value === slot.type && (typeof value !== "number" || Number.isFinite(value));
  if (!validType) {
    issues.push({
      path,
      code: "block_slot_type",
      message: `Expected ${slot.type}.`
    });
    return value;
  }
  const issue = (message) => issues.push({ path, code: "block_slot_budget", message });
  const marker = typeof value === "string" && isScaffoldMarker(value);
  if (!marker && slot.enum && !slot.enum.includes(value))
    issue("Value is not one of the declared choices.");
  if (typeof value === "string" && !marker) {
    if (slot.oneLine && /[\r\n]/.test(value))
      issue("Slot must contain one line.");
    if (slot.minLength !== void 0 && value.length < slot.minLength)
      issue(`Minimum length is ${slot.minLength}.`);
    if (slot.maxLength !== void 0 && value.length > slot.maxLength)
      issue(`Maximum length is ${slot.maxLength}.`);
    if (slot.maxWords !== void 0 && blockWordCount(value) > slot.maxWords)
      issue(`Maximum word count is ${slot.maxWords}.`);
  }
  if (typeof value === "number") {
    if (slot.minimum !== void 0 && value < slot.minimum)
      issue(`Minimum value is ${slot.minimum}.`);
    if (slot.maximum !== void 0 && value > slot.maximum)
      issue(`Maximum value is ${slot.maximum}.`);
  }
  if (Array.isArray(value)) {
    if (slot.minItems !== void 0 && value.length < slot.minItems)
      issue(`Minimum item count is ${slot.minItems}.`);
    if (slot.maxItems !== void 0 && value.length > slot.maxItems)
      issue(`Maximum item count is ${slot.maxItems}.`);
    return slot.items ? value.map(
      (v, i) => resolveBlockSlot(slot.items, v, `${path}/${i}`, issues)
    ) : value;
  }
  if (slot.type === "component" && isBlockRecord(value)) {
    const checkPlacement = (node, pointer2, depth = 0) => {
      if (depth > 64) {
        issues.push({
          path: pointer2,
          code: "block_expansion_limit",
          message: "Component slot exceeds 64 levels."
        });
        return;
      }
      if (Array.isArray(node)) {
        node.forEach(
          (item, i) => checkPlacement(item, `${pointer2}/${i}`, depth + 1)
        );
        return;
      }
      if (!isBlockRecord(node)) return;
      const props = typeof node.name === "string" && isBlockRecord(node.props) ? node.props : {};
      for (const key of BLOCK_SLOT_PLACEMENT_PROPS) {
        if (own(props, key))
          issues.push({
            path: `${pointer2}/props/${key}`,
            code: "block_slot_placement",
            message: "Block placement belongs in the definition, not in a component slot."
          });
      }
      for (const [key, item] of Object.entries(node))
        checkPlacement(item, `${pointer2}/${blockPointerKey(key)}`, depth + 1);
    };
    checkPlacement(value, path);
  }
  if (slot.type === "object" && isBlockRecord(value) && slot.properties)
    return resolveBlockSlots(slot.properties, value, path, issues);
  return value;
}
function resolveBlockSlots(slots, values, path, issues) {
  const out = {};
  for (const key of Object.keys(values)) {
    if (!own(slots, key))
      issues.push({
        path: `${path}/${blockPointerKey(key)}`,
        code: "block_unknown_slot",
        message: `Unknown slot '${key}'. Expected: ${Object.keys(slots).join(", ")}.`
      });
  }
  for (const [key, slot] of Object.entries(slots)) {
    const value = resolveBlockSlot(
      slot,
      own(values, key) ? values[key] : void 0,
      `${path}/${blockPointerKey(key)}`,
      issues
    );
    if (value !== void 0)
      Object.defineProperty(out, key, {
        value,
        enumerable: true,
        writable: true,
        configurable: true
      });
  }
  return out;
}
var DIRECTIVES = Object.fromEntries(
  Object.entries(BLOCK_DIRECTIVES).map(([key, directive]) => [
    key,
    directive.keys
  ])
);
function slotDescriptorAt(slots, pointer2) {
  let descriptor = { type: "object", properties: slots };
  for (const escaped of pointer2.slice(1).split("/")) {
    const key = escaped.replace(/~1/g, "/").replace(/~0/g, "~");
    if (descriptor?.type === "object") {
      if (!descriptor.properties) return { type: "object" };
      descriptor = own(descriptor.properties, key) ? descriptor.properties[key] : void 0;
    } else if (descriptor?.type === "array" && /^(0|[1-9]\d*)$/.test(key))
      descriptor = descriptor.items ?? { type: "object" };
    else if (descriptor?.type === "component") return { type: "object" };
    else return void 0;
  }
  return descriptor;
}
var OPERAND_DIRECTIVES = ["$if", "$each", "$count"];
var isPointer = (value) => typeof value === "string" && (value === "" || value.startsWith("/"));
var themePointerChain = (value) => isPointer(value) ? [value] : Array.isArray(value) && value.length > 0 && value.every(isPointer) ? value : void 0;
function blockOperand(value, key) {
  if (isPointer(value))
    return {
      root: OPERAND_DIRECTIVES.includes(key) ? "$slot" : key,
      pointer: value
    };
  if (!OPERAND_DIRECTIVES.includes(key) || !isBlockRecord(value))
    return void 0;
  const keys = Object.keys(value);
  const root = BLOCK_OPERAND_ROOTS.find((candidate) => candidate === keys[0]);
  if (keys.length !== 1 || !root || !isPointer(value[root])) return void 0;
  return { root, pointer: value[root] };
}
function checkTemplate(value, path, slots, issues, repeated = false, depth = 0) {
  if (depth > 64) {
    issues.push({
      path,
      code: "block_depth",
      message: "Definition exceeds 64 levels."
    });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(
      (v, i) => checkTemplate(v, `${path}/${i}`, slots, issues, repeated, depth + 1)
    );
    return;
  }
  if (!isBlockRecord(value)) return;
  const keys = Object.keys(value).filter((k) => k.startsWith("$"));
  if (keys.length) {
    const key = keys[0];
    const allowed = DIRECTIVES[key];
    if (!allowed || keys.length !== 1 || Object.keys(value).some((k) => !allowed.includes(k))) {
      issues.push({
        path,
        code: "block_invalid_binding",
        message: "Unknown or malformed block directive."
      });
      return;
    }
    if ([
      "$slot",
      "$item",
      "$theme",
      "$context",
      "$if",
      "$each",
      "$count"
    ].includes(key)) {
      const operand = blockOperand(value[key], key);
      const chain = key === "$theme" ? themePointerChain(value[key]) : void 0;
      if (!operand && !chain)
        issues.push({
          path,
          code: "block_invalid_binding",
          message: OPERAND_DIRECTIVES.includes(key) ? `${key} takes a slot pointer such as /items, or one reference: ${BLOCK_OPERAND_ROOTS.map((root) => `{ "${root}": ... }`).join(", ")}.` : key === "$theme" ? "Bindings use a JSON Pointer, e.g. /styles/source/size, or a non-empty array of them tried in order." : "Bindings use JSON Pointers, e.g. /title."
        });
      else if (operand?.root === "$slot") {
        const descriptor = slotDescriptorAt(slots, operand.pointer);
        if (!descriptor)
          issues.push({
            path,
            code: "block_unknown_binding",
            message: `No slot field '${operand.pointer}' is declared.`
          });
        else if (["$each", "$count"].includes(key) && descriptor.type !== "array")
          issues.push({
            path,
            code: "block_invalid_binding",
            message: `${key} requires an array slot.`
          });
      }
      if (operand?.root === "$item" && !repeated)
        issues.push({
          path,
          code: "block_invalid_binding",
          message: "$item is only available inside $each."
        });
      if ((key === "$slot" || key === "$item") && own(value, "props") && !isBlockRecord(value.props))
        issues.push({
          path: `${path}/props`,
          code: "block_invalid_binding",
          message: "props must be an object of component props merged beneath a component-slot value."
        });
    }
    if (key === "$join" && value.keepEmpty !== void 0 && typeof value.keepEmpty !== "boolean")
      issues.push({
        path,
        code: "block_invalid_binding",
        message: "keepEmpty must be boolean."
      });
    if (key === "$if" && !own(value, "then"))
      issues.push({
        path,
        code: "block_invalid_binding",
        message: "$if requires then."
      });
    if (key === "$each" && (!own(value, "template") || Array.isArray(value.template)))
      issues.push({
        path,
        code: "block_invalid_binding",
        message: "$each requires one template value; use a group for multiple flow children."
      });
    if (key === "$join" && (!Array.isArray(value.$join) || value.separator !== void 0 && typeof value.separator !== "string"))
      issues.push({
        path,
        code: "block_invalid_binding",
        message: "$join requires an array and an optional string separator."
      });
    if (key === "$measure" && (!["width", "height"].includes(String(value.$measure)) || !["pt", "twip", "in"].includes(String(value.unit ?? "pt")) || value.fraction !== void 0 && (typeof value.fraction !== "number" || value.fraction < 0 || value.fraction > 1)))
      issues.push({
        path,
        code: "block_invalid_binding",
        message: "$measure requires width/height, pt/twip/in and a fraction between 0 and 1."
      });
  }
  for (const [key, item] of Object.entries(value)) {
    if (key.startsWith("$") && key !== "$join") continue;
    checkTemplate(
      item,
      `${path}/${blockPointerKey(key)}`,
      slots,
      issues,
      repeated || own(value, "$each"),
      depth + 1
    );
  }
}
function readBlockDefinitions(document) {
  const value = isBlockRecord(document) && isBlockRecord(document.props) ? document.props.blocks : void 0;
  return value ?? {};
}
function validateBlockDefinitions(definitions, format, reservedNames = []) {
  if (!Value.Check(BlockDefinitionsSchema, definitions))
    return [...Value.Errors(BlockDefinitionsSchema, definitions)].slice(0, 100).map((e) => ({
      path: `/props/blocks${e.path}`,
      code: "block_invalid_definition",
      message: e.message
    }));
  const issues = [];
  for (const [name, def] of Object.entries(definitions)) {
    const path = `/props/blocks/${blockPointerKey(name)}`;
    if (reservedNames.includes(name))
      issues.push({
        path,
        code: "block_name_collision",
        message: `Block '${name}' conflicts with a registered component.`
      });
    if (format !== "docx" && def.section)
      issues.push({
        path: `${path}/section`,
        code: "block_format",
        message: "Section effects are DOCX-only."
      });
    if (format !== "pptx" && def.slide)
      issues.push({
        path: `${path}/slide`,
        code: "block_format",
        message: "Slide effects are PPTX-only."
      });
    const checkSlot = (slot, pointer2) => {
      if (slot.default !== void 0)
        resolveBlockSlot(slot, slot.default, `${pointer2}/default`, issues);
      for (const [minimum, maximum] of [
        ["minItems", "maxItems"],
        ["minLength", "maxLength"],
        ["minimum", "maximum"]
      ]) {
        if (slot[minimum] !== void 0 && slot[maximum] !== void 0 && slot[minimum] > slot[maximum])
          issues.push({
            path: pointer2,
            code: "block_invalid_definition",
            message: `${minimum} exceeds ${maximum}.`
          });
      }
      if (slot.items) checkSlot(slot.items, `${pointer2}/items`);
      for (const [key, nested] of Object.entries(slot.properties ?? {}))
        checkSlot(nested, `${pointer2}/properties/${blockPointerKey(key)}`);
    };
    for (const [key, slot] of Object.entries(def.slots))
      checkSlot(slot, `${path}/slots/${blockPointerKey(key)}`);
    checkTemplate(def.body, `${path}/body`, def.slots, issues);
    if (def.section)
      checkTemplate(def.section, `${path}/section`, def.slots, issues);
    if (def.slide) checkTemplate(def.slide, `${path}/slide`, def.slots, issues);
  }
  issues.push(...checkDefinitionReferences(definitions));
  return issues;
}
function definitionReferences(value, path, found, guarded = false) {
  if (Array.isArray(value)) {
    value.forEach(
      (v, i) => definitionReferences(v, `${path}/${i}`, found, guarded)
    );
    return;
  }
  if (!isBlockRecord(value)) return;
  if (value.enabled === false) return;
  if (value.name === "block" && isBlockRecord(value.props) && typeof value.props.ref === "string")
    found.push({ ref: value.props.ref, path: `${path}/props/ref`, guarded });
  if ("$if" in value && ("then" in value || "else" in value)) {
    definitionReferences(value.$if, `${path}/$if`, found, guarded);
    const branch = (key) => {
      const out = [];
      if (key in value)
        definitionReferences(
          value[key],
          `${path}/${blockPointerKey(key)}`,
          out,
          guarded
        );
      return out;
    };
    const taken = branch("then");
    const otherwise = branch("else");
    const inEvery = new Set(
      "then" in value && "else" in value ? taken.filter((reference) => !reference.guarded).map((reference) => reference.ref).filter(
        (ref) => otherwise.some(
          (reference) => !reference.guarded && reference.ref === ref
        )
      ) : []
    );
    for (const reference of [...taken, ...otherwise])
      found.push({
        ...reference,
        guarded: reference.guarded || !inEvery.has(reference.ref)
      });
    return;
  }
  const directive = Object.keys(value).some(
    (key) => key.startsWith("$") && key !== "$join"
  );
  for (const [key, child] of Object.entries(value))
    definitionReferences(
      child,
      `${path}/${blockPointerKey(key)}`,
      found,
      guarded || directive
    );
}
function checkDefinitionReferences(definitions) {
  const issues = [];
  const references = /* @__PURE__ */ new Map();
  for (const [name, def] of Object.entries(definitions)) {
    const path = `/props/blocks/${blockPointerKey(name)}`;
    const found = [];
    definitionReferences(def.body, `${path}/body`, found);
    if (def.section)
      definitionReferences(def.section, `${path}/section`, found);
    if (def.slide) definitionReferences(def.slide, `${path}/slide`, found);
    references.set(name, found);
    for (const reference of found)
      if (!own(definitions, reference.ref))
        issues.push({
          path: reference.path,
          code: "block_unknown_reference",
          message: `Block '${reference.ref}' is not defined in this document.`
        });
  }
  const done = /* @__PURE__ */ new Set();
  const trail = [];
  const reported = /* @__PURE__ */ new Set();
  const visit = (name) => {
    if (done.has(name)) return;
    trail.push(name);
    for (const reference of references.get(name) ?? []) {
      if (reference.guarded || !own(definitions, reference.ref)) continue;
      const start = trail.indexOf(reference.ref);
      if (start !== -1) {
        if (reported.has(`${name}\0${reference.ref}`)) continue;
        reported.add(`${name}\0${reference.ref}`);
        issues.push({
          path: reference.path,
          code: "block_expansion_limit",
          message: `Block '${reference.ref}' invokes itself on every expansion (${[
            ...trail.slice(start),
            reference.ref
          ].join(" \u2192 ")}), so expansion could only stop at its limit.`
        });
        continue;
      }
      visit(reference.ref);
    }
    trail.pop();
    done.add(name);
  };
  for (const name of Object.keys(definitions)) visit(name);
  return issues;
}
function validateBlockInvocations(document, definitions, format, reservedNames = []) {
  const issues = validateBlockDefinitions(definitions, format, reservedNames);
  if (issues.length) return issues;
  const walk = (v, path) => {
    if (Array.isArray(v)) {
      v.forEach((item, i) => walk(item, `${path}/${i}`));
      return;
    }
    if (!isBlockRecord(v) || v.enabled === false) return;
    if (v.name === "block" && isBlockRecord(v.props) && typeof v.props.ref === "string") {
      const def = own(definitions, v.props.ref) ? definitions[v.props.ref] : void 0;
      if (!def)
        issues.push({
          path: `${path}/props/ref`,
          code: "block_unknown_reference",
          message: `Block '${v.props.ref}' is not defined in this document.`
        });
      else {
        if (v.props.slots === void 0 || isBlockRecord(v.props.slots))
          resolveBlockSlots(
            def.slots,
            v.props.slots ?? {},
            `${path}/props/slots`,
            issues
          );
        if (def.section && !/^\/children\/\d+\/children\/\d+$/.test(path))
          issues.push({
            path,
            code: "invalid_placement",
            message: "A block with section effects must be a direct child of a top-level section."
          });
        if (def.slide && !/^\/children\/\d+\/children\/\d+$/.test(path))
          issues.push({
            path,
            code: "invalid_placement",
            message: "A block with slide effects must be a direct child of a slide."
          });
      }
    }
    for (const [key, item] of Object.entries(v)) {
      if (path === "/props" && key === "blocks") continue;
      walk(item, `${path}/${blockPointerKey(key)}`);
    }
  };
  walk(document, "");
  return issues;
}
var JsonBlockEvaluator = class {
  constructor(definitions, options) {
    this.definitions = definitions;
    this.options = options;
    const issues = validateBlockDefinitions(
      definitions,
      options.format,
      options.reservedNames
    );
    if (issues.length) throw new BlockEvaluationError(issues);
  }
  sourceMap = {};
  blocks = [];
  /** Compiled pointers holding a registered component's output. */
  pluginOutputs = [];
  blockSources = /* @__PURE__ */ new Set();
  nodes = 0;
  guard(path, depth) {
    if (depth > 64 || ++this.nodes > 5e4)
      fail(
        path,
        "block_expansion_limit",
        "Block expansion exceeds the depth/node limit (64/50000)."
      );
  }
  /**
   * A directive operand and the authored pointer it came from: the slot the
   * pointer form names, or the current item, slot or context a reference
   * object names. Definitions are validated before this runs, so a malformed
   * operand cannot reach it.
   */
  operand(raw, key, env) {
    const { root, pointer: pointer2 } = blockOperand(raw, key);
    const { value, source } = this.reference(root, pointer2, env);
    return {
      value,
      source,
      // Element i of the array is authored at pointer/i, looked up the same
      // way: a slot the enclosing invocation built from its own repeat maps
      // element by element, and pointer + "/i" is not the same as that.
      element: (index) => this.reference(root, `${pointer2}/${index}`, env).source
    };
  }
  /**
   * What a reference reads and where the author wrote it. One resolution for
   * the binding form and the operand form, so `{ "$context": ... }` is
   * attributed the same way whichever directive carries it.
   */
  reference(root, pointer2, env) {
    if (root === "$item")
      return {
        value: blockValueAt(env.item, pointer2),
        source: `${env.itemSource ?? env.source}${pointer2}`
      };
    if (root === "$context") {
      const authored = toAuthoredBlockPointer(
        env.contextSources ?? {},
        pointer2
      );
      return {
        value: blockValueAt(env.context, pointer2),
        source: authored === pointer2 ? env.source : authored
      };
    }
    if (root === "$theme")
      return {
        value: blockValueAt(this.options.theme, pointer2),
        source: env.source
      };
    return {
      value: blockValueAt(env.slots, pointer2),
      source: env.slotSources ? toAuthoredBlockPointer(env.slotSources, pointer2) : `${env.source}/props/slots${pointer2}`
    };
  }
  evaluate(value, env, out, definitionPath, depth = 0) {
    this.guard(env.source, depth);
    this.sourceMap[out] = env.source;
    if (Array.isArray(value)) {
      const result2 = [];
      value.forEach((v, i) => {
        const evaluated = this.evaluate(
          v,
          env,
          `${out}/${result2.length}`,
          `${definitionPath}/${i}`,
          depth + 1
        );
        if (evaluated !== void 0) {
          if (isBlockRecord(v) && ("$if" in v || "$each" in v) && Array.isArray(evaluated)) {
            const base = `${out}/${result2.length}`;
            const maps = Object.entries(this.sourceMap).filter(
              ([key]) => key.startsWith(`${base}/`)
            );
            for (const [key] of maps) delete this.sourceMap[key];
            for (const [key, source] of maps) {
              const rest = key.slice(base.length + 1);
              const [index, ...suffix] = rest.split("/");
              this.sourceMap[`${out}/${result2.length + Number(index)}${suffix.length ? "/" + suffix.join("/") : ""}`] = source;
            }
            result2.push(...evaluated);
          } else result2.push(evaluated);
        }
      });
      return result2;
    }
    if (!isBlockRecord(value)) return value;
    if ("$slot" in value || "$item" in value || "$theme" in value || "$context" in value) {
      const key = ["$slot", "$item", "$theme", "$context"].find(
        (k) => k in value
      );
      const chain = (key === "$theme" ? themePointerChain(value[key]) : void 0) ?? [];
      const pointer2 = chain[0] ?? value[key];
      let found;
      let source = env.source;
      for (const candidate of chain.length ? chain : [pointer2]) {
        const read = this.reference(key, candidate, env);
        source = read.source;
        if (read.value !== void 0) {
          found = read.value;
          break;
        }
      }
      this.sourceMap[out] = source;
      let result2 = found !== void 0 ? structuredClone(found) : void 0;
      if (result2 === void 0 && own(value, "default"))
        result2 = this.evaluate(
          value.default,
          env,
          out,
          `${definitionPath}/default`,
          depth + 1
        );
      if (result2 === void 0 && key === "$theme")
        return fail(
          definitionPath,
          "block_unknown_theme_binding",
          `Theme value '${chain.length > 1 ? chain.join(", ") : pointer2}' is missing; declare a fallback or use an existing token.`
        );
      if ((key === "$slot" || key === "$item") && own(value, "props") && isBlockRecord(result2) && typeof result2.name === "string") {
        const origin = this.sourceMap[out];
        const defaults = this.evaluate(
          value.props,
          env,
          `${out}/props`,
          `${definitionPath}/props`,
          depth + 1
        );
        const authored = isBlockRecord(result2.props) ? result2.props : {};
        for (const propKey of Object.keys(authored)) {
          const pointerKey = `${out}/props/${blockPointerKey(propKey)}`;
          for (const mapped of Object.keys(this.sourceMap))
            if (mapped === pointerKey || mapped.startsWith(`${pointerKey}/`))
              delete this.sourceMap[mapped];
          this.sourceMap[pointerKey] = `${origin}/props/${blockPointerKey(propKey)}`;
        }
        result2 = {
          ...result2,
          props: {
            ...isBlockRecord(defaults) ? defaults : {},
            ...authored
          }
        };
      }
      return result2;
    }
    if ("$if" in value) {
      const operand = this.operand(value.$if, "$if", env);
      const branch = present(operand.value) ? value.then : value.else;
      const result2 = this.evaluate(branch, env, out, definitionPath, depth + 1);
      const literal = !isBlockRecord(branch) || !Object.keys(branch).some((k) => k.startsWith("$"));
      if (literal && this.sourceMap[out] === env.source)
        this.sourceMap[out] = operand.source;
      return result2;
    }
    if ("$count" in value) {
      const operand = this.operand(value.$count, "$count", env);
      if (!Array.isArray(operand.value))
        return fail(
          operand.source,
          "block_slot_type",
          "$count requires an array."
        );
      return operand.value.length;
    }
    if ("$each" in value) {
      const operand = this.operand(value.$each, "$each", env);
      const list = operand.value;
      if (!Array.isArray(list))
        return fail(
          operand.source,
          "block_slot_type",
          "$each requires an array."
        );
      const result2 = [];
      list.forEach((item, i) => {
        const pointer2 = `${out}/${result2.length}`;
        const evaluated = this.evaluate(
          value.template,
          {
            ...env,
            item,
            itemSource: operand.element(i)
          },
          pointer2,
          `${definitionPath}/template`,
          depth + 1
        );
        if (evaluated !== void 0) {
          if (this.sourceMap[pointer2] === env.source)
            this.sourceMap[pointer2] = operand.element(i);
          result2.push(evaluated);
        } else
          for (const key of Object.keys(this.sourceMap)) {
            if (key === pointer2 || key.startsWith(`${pointer2}/`))
              delete this.sourceMap[key];
          }
      });
      return result2;
    }
    if ("$join" in value) {
      const values = value.$join.map(
        (v, i) => this.evaluate(
          v,
          env,
          `${out}/${i}`,
          `${definitionPath}/$join/${i}`,
          depth + 1
        )
      );
      const first = values.findIndex(present);
      if (first >= 0) this.sourceMap[out] = this.sourceMap[`${out}/${first}`];
      return (value.keepEmpty === true ? values : values.filter(present)).map((v) => String(v ?? "")).join(String(value.separator ?? ""));
    }
    if ("$measure" in value) {
      if (!this.options.measure)
        return fail(
          definitionPath,
          "block_unsupported_operation",
          "This format does not support $measure."
        );
      return this.options.measure(
        value.$measure,
        value.unit ?? "pt",
        env.context
      ) * Number(value.fraction ?? 1);
    }
    const result = {};
    for (const [key, item] of Object.entries(value)) {
      const evaluated = this.evaluate(
        item,
        env,
        `${out}/${blockPointerKey(key)}`,
        `${definitionPath}/${blockPointerKey(key)}`,
        depth + 1
      );
      if (evaluated !== void 0)
        Object.defineProperty(result, key, {
          value: evaluated,
          enumerable: true,
          configurable: true,
          writable: true
        });
    }
    return result;
  }
  expand(value, path = "", depth = 0) {
    this.guard(path, depth);
    if (Array.isArray(value))
      return value.map((v, i) => this.expand(v, `${path}/${i}`, depth + 1));
    if (!isBlockRecord(value)) return value;
    if (value.name === "block" && value.enabled !== false) {
      if (!isBlockRecord(value.props) || typeof value.props.ref !== "string")
        return fail(
          path,
          "block_invalid_invocation",
          "A block requires props.ref and optional props.slots."
        );
      if (Object.keys(value.props).some(
        (key) => !["ref", "slots"].includes(key)
      ) || value.props.slots !== void 0 && !isBlockRecord(value.props.slots))
        return fail(
          path,
          "block_invalid_invocation",
          "Block props accept only ref and an object of slots."
        );
      const def = own(this.definitions, value.props.ref) ? this.definitions[value.props.ref] : void 0;
      if (!def)
        return fail(
          `${path}/props/ref`,
          "block_unknown_reference",
          `Block '${value.props.ref}' is not defined in this document.`
        );
      const issues = [];
      const source = toAuthoredBlockPointer(this.sourceMap, path);
      const slotsPath = `${path}/props/slots`;
      const slots = resolveBlockSlots(
        def.slots,
        value.props.slots ?? {},
        slotsPath,
        issues
      );
      if (issues.length)
        throw new BlockEvaluationError(
          issues.map((issue) => ({
            ...issue,
            path: toAuthoredBlockPointer(this.sourceMap, issue.path)
          }))
        );
      const slotSources = Object.fromEntries([
        ["", toAuthoredBlockPointer(this.sourceMap, slotsPath)],
        ...Object.entries(this.sourceMap).filter(([key]) => key.startsWith(`${slotsPath}/`)).map(([key, origin]) => [key.slice(slotsPath.length), origin])
      ]);
      const env = {
        slots,
        slotSources,
        source,
        definition: `/props/blocks/${blockPointerKey(value.props.ref)}`,
        context: this.options.contextAt?.(path) ?? this.options.context ?? {},
        contextSources: this.options.contextSources
      };
      if (def.section)
        this.options.onSection?.({
          settings: def.section,
          environment: env,
          path
        });
      if (def.slide)
        this.options.onSlide?.({ settings: def.slide, environment: env, path });
      if (!this.blockSources.has(source)) {
        this.blockSources.add(source);
        this.blocks.push(source);
      }
      const children = this.evaluate(
        def.body,
        env,
        `${path}/children`,
        `${env.definition}/body`,
        depth + 1
      );
      return {
        name: "group",
        ...value.id !== void 0 && { id: value.id },
        children: this.expand(children, `${path}/children`, depth + 1)
      };
    }
    if (value.enabled === false) return { ...value };
    const result = { ...value };
    for (const [key, item] of Object.entries(value)) {
      if (path === "/props" && key === "blocks") continue;
      Object.defineProperty(result, key, {
        value: this.expand(item, `${path}/${blockPointerKey(key)}`, depth + 1),
        enumerable: true,
        configurable: true,
        writable: true
      });
    }
    return result;
  }
};

// src/blocks/metadata.ts
import { isScaffoldMarker as isScaffoldMarker2 } from "@json-to-office/quality";
import { Value as Value2 } from "@sinclair/typebox/value";
function blockSlotsJsonSchema(definition) {
  return {
    type: "object",
    additionalProperties: false,
    properties: Object.fromEntries(
      Object.entries(definition.slots).map(([key, slot]) => [
        key,
        blockSlotJsonSchema(slot)
      ])
    ),
    required: Object.entries(definition.slots).filter(([, slot]) => slot.required && slot.default === void 0).map(([key]) => key)
  };
}
function documentBlockMetadata(document) {
  const definitions = readBlockDefinitions(document);
  if (!Value2.Check(BlockDefinitionsSchema, definitions))
    return { definitions: [], invocations: [], invalidDefinitions: true };
  const invocations2 = [];
  const walk = (value, path) => {
    if (Array.isArray(value)) {
      value.forEach((item, i) => walk(item, `${path}/${i}`));
      return;
    }
    if (!isBlockRecord(value)) return;
    if (value.name === "block" && isBlockRecord(value.props) && typeof value.props.ref === "string")
      invocations2.push({
        ref: value.props.ref,
        path,
        slotsPath: `${path}/props/slots`,
        defined: Object.prototype.hasOwnProperty.call(
          definitions,
          value.props.ref
        )
      });
    for (const [key, item] of Object.entries(value)) {
      if (path === "/props" && key === "blocks") continue;
      walk(item, `${path}/${blockPointerKey(key)}`);
    }
  };
  walk(document, "");
  return {
    definitions: Object.entries(definitions).map(([name, definition]) => ({
      name,
      definitionPointer: `/props/blocks/${blockPointerKey(name)}`,
      definition,
      slotsSchema: blockSlotsJsonSchema(definition)
    })),
    invocations: invocations2,
    invalidDefinitions: false
  };
}
function visitInvocationSlots(document, blocks, visit) {
  const definitions = readBlockDefinitions(document);
  for (const path of blocks) {
    const node = blockValueAt(document, path);
    if (!isBlockRecord(node) || !isBlockRecord(node.props) || typeof node.props.ref !== "string")
      continue;
    const ref = node.props.ref;
    const definition = definitions[ref];
    if (!definition) continue;
    const walk = (slot, value, pointer2, name) => {
      visit(ref, slot, value, pointer2, name);
      if (isBlockRecord(value) && slot.properties) {
        for (const [key, property] of Object.entries(slot.properties)) {
          walk(
            property,
            blockValueAt(value, `/${blockPointerKey(key)}`),
            `${pointer2}/${blockPointerKey(key)}`,
            `${name}.${key}`
          );
        }
      }
      if (Array.isArray(value) && slot.items)
        value.forEach(
          (item, i) => walk(slot.items, item, `${pointer2}/${i}`, name)
        );
    };
    for (const [name, slot] of Object.entries(definition.slots)) {
      const authored = blockValueAt(
        node.props.slots,
        `/${blockPointerKey(name)}`
      );
      walk(
        slot,
        authored === void 0 && slot.default !== void 0 ? slot.default : authored,
        `${path}/props/slots/${blockPointerKey(name)}`,
        name
      );
    }
  }
}
function blockSlotBudgets(document, blocks) {
  const result = [];
  visitInvocationSlots(document, blocks, (ref, slot, value, pointer2, name) => {
    if (typeof value === "string" && slot.maxWords !== void 0 && !isScaffoldMarker2(value))
      result.push({
        block: ref,
        slot: name,
        path: pointer2,
        words: blockWordCount(value),
        maxWords: slot.maxWords
      });
  });
  return result;
}
function blockSlotRoles(document, blocks) {
  const result = [];
  visitInvocationSlots(document, blocks, (ref, slot, value, pointer2, name) => {
    if (!slot.role) return;
    result.push({
      block: ref,
      invocation: pointer2.replace(/\/props\/slots\/.*$/, ""),
      slot: name,
      role: slot.role,
      path: pointer2,
      value
    });
  });
  return result;
}

// src/blocks/schema-types.ts
var allTypes = [
  "null",
  "boolean",
  "number",
  "string",
  "array",
  "object"
];
var intersection = (a, b) => new Set([...a].filter((value) => b.has(value)));
var union = (sets) => new Set(sets.flatMap((set) => [...set]));
var typeOf = (value) => value === null ? "null" : Array.isArray(value) ? "array" : typeof value;
function possibleValueTypes(schema, resolve, seen = /* @__PURE__ */ new Set()) {
  if (schema === false) return /* @__PURE__ */ new Set();
  if (schema === true || seen.has(schema)) return new Set(allTypes);
  const negated = schema.not;
  if (negated === true || negated && typeof negated === "object" && Object.keys(negated).length === 0)
    return /* @__PURE__ */ new Set();
  const next = new Set(seen).add(schema);
  let types = new Set(allTypes);
  if (schema.type) {
    const declared = Array.isArray(schema.type) ? schema.type : [schema.type];
    types = intersection(
      types,
      new Set(
        allTypes.filter(
          (type) => declared.includes(type) || type === "number" && declared.includes("integer")
        )
      )
    );
  }
  if (Object.hasOwn(schema, "const"))
    types = intersection(types, /* @__PURE__ */ new Set([typeOf(schema.const)]));
  if (Array.isArray(schema.enum))
    types = intersection(types, new Set(schema.enum.map(typeOf)));
  if (typeof schema.$ref === "string") {
    const target = resolve(schema.$ref);
    if (target !== void 0)
      types = intersection(types, possibleValueTypes(target, resolve, next));
  }
  for (const key of ["anyOf", "oneOf"]) {
    if (Array.isArray(schema[key]))
      types = intersection(
        types,
        union(
          schema[key].map(
            (branch) => possibleValueTypes(branch, resolve, next)
          )
        )
      );
  }
  if (Array.isArray(schema.allOf))
    for (const branch of schema.allOf)
      types = intersection(types, possibleValueTypes(branch, resolve, next));
  if (negated && typeof negated === "object" && negated.type && Object.keys(negated).every(
    (key) => ["type", "description", "title", "$comment"].includes(key)
  )) {
    const excluded = (Array.isArray(negated.type) ? negated.type : [negated.type]).filter((type) => type !== "integer");
    types = new Set([...types].filter((type) => !excluded.includes(type)));
  }
  return types;
}
function arrayItemSchema(schema, resolve, seen = /* @__PURE__ */ new Set()) {
  if (schema === false) return false;
  if (schema === true || seen.has(schema)) return {};
  const next = new Set(seen).add(schema);
  const constraints = [];
  if (typeof schema.$ref === "string") {
    const target = resolve(schema.$ref);
    if (target !== void 0)
      constraints.push(arrayItemSchema(target, resolve, next));
  }
  if (schema.items !== void 0)
    constraints.push(
      Array.isArray(schema.items) ? {
        anyOf: [
          ...schema.items,
          ...schema.additionalItems === false ? [] : [schema.additionalItems ?? {}]
        ]
      } : schema.items
    );
  for (const key of ["anyOf", "oneOf"])
    if (Array.isArray(schema[key])) {
      constraints.push({
        anyOf: schema[key].filter(
          (branch) => possibleValueTypes(branch, resolve).has("array")
        ).map(
          (branch) => arrayItemSchema(branch, resolve, next)
        )
      });
    }
  if (Array.isArray(schema.allOf))
    constraints.push(
      ...schema.allOf.map(
        (branch) => arrayItemSchema(branch, resolve, next)
      )
    );
  return constraints.length === 0 ? {} : constraints.length === 1 ? constraints[0] : { allOf: constraints };
}

// src/blocks/authoring-schema.ts
var object = (properties, required) => ({
  type: "object",
  properties,
  required,
  additionalProperties: false
});
var pointer = (description) => ({
  type: "string",
  pattern: "^(|/.*)$",
  description
});
var pointerChain = (description) => ({
  anyOf: [
    pointer(description),
    {
      type: "array",
      minItems: 1,
      items: pointer(description),
      description
    }
  ],
  description
});
var referenceDescriptions = (format) => ({
  $slot: "Read a named input slot by JSON Pointer, e.g. /title or /client/name.",
  $item: "Read the current $each entry by JSON Pointer. Use an empty string for the whole entry or /title for a property.",
  $theme: 'Read the active theme by JSON Pointer, e.g. /colors/primary, or by a list of pointers tried in order, e.g. ["/chrome/sourceLine/color", "/styles/source/color"] \u2014 the chrome recipe if the theme sets one, else the type role. A missing value requires a default.',
  $context: format === "pptx" ? "Read deck or slide context by JSON Pointer, e.g. /document/title, /slide/width or /slide/index." : "Read document or section context by JSON Pointer, e.g. /document/title or /section/tracker."
});
var measureDescriptions = (format) => format === "pptx" ? {
  axis: "Measure the slide canvas width or height, in the unit given.",
  unit: "Measurement unit: points, twentieths of a point, or inches. Defaults to pt; use in for frame coordinates."
} : {
  axis: "Measure the usable page width or height after margins, using the containing section\u2019s page settings.",
  unit: "Measurement unit: points, twentieths of a point, or inches. Defaults to pt."
};
var describe = (schema, description) => ({
  ...typeof schema === "boolean" ? { allOf: [schema] } : schema,
  description
});
var metadata = (schema) => typeof schema === "boolean" ? {} : {
  ...schema.description && { description: schema.description },
  ...schema.markdownDescription && {
    markdownDescription: schema.markdownDescription
  }
};
var hasKey = (key) => ({ type: "object", required: [key] });
var directiveNames = Object.keys(BLOCK_DIRECTIVES);
function createBlockAuthoringSchema(definitions, componentDefinition, excludedComponents = [], format = "docx") {
  const prefix = `BlockTemplate_${componentDefinition}`;
  const references = referenceDescriptions(format);
  const measure = measureDescriptions(format);
  const operand = (description) => ({
    description,
    anyOf: [
      pointer(description),
      ...BLOCK_OPERAND_ROOTS.map(
        (root) => object({ [root]: pointer(references[root]) }, [root])
      )
    ]
  });
  const bodyName = `${prefix}_Body`;
  const ref = (name) => ({ $ref: `#/definitions/${name}` });
  if (definitions[bodyName]) return ref(bodyName);
  const originals = { ...definitions };
  const source = originals[componentDefinition];
  originals[componentDefinition] = {
    ...source,
    anyOf: (source.anyOf ?? [source]).filter(
      (branch) => !excludedComponents.includes(branch.properties?.name?.const)
    )
  };
  const resolve = (pointer2) => {
    if (!pointer2.startsWith("#/definitions/")) return void 0;
    let node = originals;
    for (const key of pointer2.slice("#/definitions/".length).split("/")) {
      const decoded = key.replace(/~1/g, "/").replace(/~0/g, "~");
      if (!node || typeof node !== "object" || !Object.hasOwn(node, decoded))
        return void 0;
      node = node[decoded];
    }
    return typeof node === "boolean" || node && typeof node === "object" ? node : void 0;
  };
  const values = /* @__PURE__ */ new Map();
  const literals = /* @__PURE__ */ new Map();
  let nextId = 0;
  const componentRef = ref(componentDefinition);
  const shared = /* @__PURE__ */ new Map();
  const share = (schema) => {
    const key = JSON.stringify(schema);
    let name = shared.get(key);
    if (!name) {
      name = `${prefix}_Shared${nextId++}`;
      shared.set(key, name);
      definitions[name] = schema;
    }
    return ref(name);
  };
  const presence = Object.fromEntries(
    directiveNames.map((key) => [key, share(hasKey(key))])
  );
  const anyDirective = share({ anyOf: Object.values(presence) });
  const starterPrefixes = [
    .../* @__PURE__ */ new Set([
      "",
      ...directiveNames.flatMap(
        (key) => Array.from(
          { length: key.length - 1 },
          (_, index) => key.slice(0, index + 1)
        )
      )
    ])
  ];
  const starterObject = share({
    type: "object",
    // An enum here would itself become a list of bogus property suggestions.
    propertyNames: {
      pattern: `^(?:${starterPrefixes.map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})$`
    }
  });
  function literal(schema) {
    if (typeof schema === "boolean") return schema;
    const key = JSON.stringify(schema);
    const cached = literals.get(key);
    if (cached) return { ...ref(cached), ...metadata(schema) };
    const name = `${prefix}_Literal${nextId++}`;
    literals.set(key, name);
    const result = definitions[name] = {};
    Object.assign(result, schema);
    if (typeof schema.$ref === "string") {
      const target = resolve(schema.$ref);
      if (target !== void 0) {
        const transformed = literal(target);
        if (typeof transformed === "object") result.$ref = transformed.$ref;
        else {
          delete result.$ref;
          if (!transformed) result.not = {};
        }
      }
    }
    if (schema.properties)
      result.properties = Object.fromEntries(
        Object.entries(
          schema.properties
        ).map(([key2, value]) => [
          key2,
          // Literal discriminators retain canonical component/version dispatch
          // and their individual choice descriptions.
          ["name", "version"].includes(key2) && typeof value === "object" && typeof value.const === "string" ? value : author(value)
        ])
      );
    if (schema.patternProperties)
      result.patternProperties = Object.fromEntries(
        Object.entries(
          schema.patternProperties
        ).map(([key2, value]) => [key2, author(value)])
      );
    if (schema.items !== void 0)
      result.items = Array.isArray(schema.items) ? schema.items.map((item) => author(item, true)) : author(schema.items, true);
    for (const key2 of ["additionalProperties", "additionalItems"])
      if (typeof schema[key2] === "object")
        result[key2] = author(schema[key2], key2 === "additionalItems");
    for (const key2 of ["anyOf", "oneOf", "allOf"])
      if (Array.isArray(schema[key2]))
        result[key2] = schema[key2].map((branch) => {
          const transformed = literal(branch);
          return typeof branch === "object" && typeof branch.properties?.name?.const === "string" && typeof transformed === "object" && transformed.$ref ? definitions[transformed.$ref.slice("#/definitions/".length)] : transformed;
        });
    for (const key2 of ["then", "else"])
      if (schema[key2] !== void 0) result[key2] = literal(schema[key2]);
    return { ...ref(name), ...metadata(schema) };
  }
  function author(input, sequence = false) {
    if (input === false) return false;
    const annotations = metadata(input);
    const schema = typeof input === "object" ? { ...input } : input;
    if (typeof schema === "object") {
      delete schema.description;
      delete schema.markdownDescription;
    }
    const key = `${sequence ? "sequence" : "value"}:${JSON.stringify(schema)}`;
    const cached = values.get(key);
    if (cached) return { ...ref(cached), ...annotations };
    const name = `${prefix}_Value${nextId++}`;
    values.set(key, name);
    definitions[name] = {};
    const self = ref(name);
    const types = possibleValueTypes(schema, resolve);
    if (types.size === 0) {
      definitions[name] = { allOf: [literal(schema)] };
      return self;
    }
    const value = () => sequence ? author(schema) : self;
    const branch = () => sequence ? { anyOf: [self, { type: "array", items: self }] } : self;
    const item = () => sequence ? value() : author(arrayItemSchema(schema, resolve));
    const specs = {};
    for (const directive of directiveNames) {
      const result = BLOCK_DIRECTIVES[directive].result;
      if (result !== "dynamic" && !types.has(result) && !(sequence && result === "array"))
        continue;
      switch (directive) {
        case "$slot":
        case "$item":
        case "$theme":
        case "$context":
          specs[directive] = object(
            {
              [directive]: directive === "$theme" ? pointerChain(references[directive]) : pointer(references[directive]),
              default: describe(
                value(),
                "Fallback value or binding used only when the referenced value is missing. Null, false and empty values do not trigger it."
              ),
              ...directive === "$slot" || directive === "$item" ? {
                props: {
                  type: "object",
                  description: "Component props merged beneath a component-slot value. Put placement (x, y, w, h, grid) and styling defaults here; the slot content may override styling but never placement."
                }
              } : {}
            },
            [directive]
          );
          break;
        case "$if":
          specs[directive] = object(
            {
              $if: operand(
                'Test a slot by JSON Pointer, e.g. /subtitle, or a reference such as { "$item": "/numeric" } or { "$theme": "/chrome/keyTakeaways/fill" }. Missing, null, false, empty text and empty arrays select else; zero selects then.'
              ),
              then: describe(
                branch(),
                "Value or components to emit when the slot tested by $if is present."
              ),
              else: describe(
                branch(),
                "Value or components to emit otherwise. Omit to produce no output."
              )
            },
            ["$if", "then"]
          );
          break;
        case "$each":
          specs[directive] = object(
            {
              $each: operand(
                'Repeat template for each entry in an array slot, e.g. /items, or in an array named by a reference, { "$item": "/cells" } or { "$theme": "/palette/chart" }. Read the current entry with $item.'
              ),
              template: describe(
                item(),
                "One template evaluated per array entry. Use $item for the current entry and a group for multiple components."
              )
            },
            ["$each", "template"]
          );
          break;
        case "$count":
          specs[directive] = object(
            {
              $count: operand(
                'Return the number of entries in an array slot, e.g. /items, or in an array of the current entry, { "$item": "/cells" }.'
              )
            },
            ["$count"]
          );
          break;
        case "$join":
          specs[directive] = object(
            {
              $join: {
                type: "array",
                items: author({}),
                description: "Evaluate these values or bindings and join them as text. Empty values are skipped unless keepEmpty is true."
              },
              separator: {
                type: "string",
                description: "Text inserted between joined values. Defaults to an empty string."
              },
              keepEmpty: {
                type: "boolean",
                description: "Keep missing, null, false, empty text and empty arrays in the join. Defaults to false."
              }
            },
            ["$join"]
          );
          break;
        case "$measure":
          specs[directive] = object(
            {
              $measure: {
                enum: ["width", "height"],
                description: measure.axis
              },
              fraction: {
                type: "number",
                minimum: 0,
                maximum: 1,
                description: "Fraction of the measured dimension, from 0 to 1. Defaults to 1."
              },
              unit: {
                enum: ["pt", "twip", "in"],
                description: measure.unit
              }
            },
            ["$measure"]
          );
          break;
        default: {
          const exhaustive = directive;
          throw new Error(
            `Missing authoring schema for directive ${exhaustive}`
          );
        }
      }
    }
    definitions[name] = {
      allOf: [
        {
          if: anyDirective,
          then: {
            allOf: directiveNames.map((directive) => ({
              if: presence[directive],
              then: specs[directive] ? share({
                ...specs[directive],
                properties: Object.fromEntries(
                  Object.entries(specs[directive].properties).map(
                    ([key2, property]) => [key2, share(property)]
                  )
                )
              }) : false
            }))
          },
          else: literal(schema)
        },
        {
          // Keep starters while the first key is empty or a partial directive
          // ("$", "$sl", ...). Any ordinary or completed key ends this phase.
          // Prefixes come from the evaluator's directive registry.
          if: starterObject,
          then: {
            properties: Object.fromEntries(
              Object.entries(specs).map(([key2, spec]) => [
                key2,
                share(spec.properties[key2])
              ])
            )
          }
        }
      ]
    };
    return { ...self, ...annotations };
  }
  definitions[bodyName] = author(componentRef, true);
  return ref(bodyName);
}

// src/blocks/compose.ts
async function composeBlocksWithPlugins(evaluator, document, options) {
  const preserve = options.preserve ?? /* @__PURE__ */ new Set();
  let visited = 0;
  const walk = async (value, path, depth) => {
    if (depth > 64 || ++visited > 1e5)
      throw new BlockEvaluationError([
        {
          path: toAuthoredBlockPointer(evaluator.sourceMap, path),
          code: "block_expansion_limit",
          message: "Combined plugin/block expansion exceeds depth/node limits (64/100000)."
        }
      ]);
    if (Array.isArray(value)) {
      const children = [];
      for (let i = 0; i < value.length; i++)
        children.push(await walk(value[i], `${path}/${i}`, depth + 1));
      return {
        standard: children.map((c) => c.standard),
        preserved: children.map((c) => c.preserved)
      };
    }
    if (!isBlockRecord(value) || value.enabled === false)
      return { standard: value, preserved: value };
    if (value.name === "block")
      return walk(evaluator.expand(value, path, depth), path, depth + 1);
    const standard = { ...value };
    const kept = { ...value };
    for (const [key, item] of Object.entries(value)) {
      if (path === "/props" && key === "blocks") continue;
      const processed = await walk(item, `${path}/${key}`, depth + 1);
      Object.defineProperty(standard, key, {
        value: processed.standard,
        enumerable: true,
        configurable: true,
        writable: true
      });
      Object.defineProperty(kept, key, {
        value: processed.preserved,
        enumerable: true,
        configurable: true,
        writable: true
      });
    }
    if (typeof value.name === "string" && options.plugins.has(value.name)) {
      const source = toAuthoredBlockPointer(evaluator.sourceMap, path);
      const emitted = await options.render(standard, source);
      evaluator.sourceMap[`${path}/children`] = source;
      evaluator.pluginOutputs.push(`${path}/children`);
      const processed = await walk(emitted, `${path}/children`, depth + 1);
      return {
        standard: { name: "group", children: processed.standard },
        preserved: preserve.has(value.name) ? value : { name: "group", children: processed.preserved }
      };
    }
    return { standard, preserved: kept };
  };
  return walk(document, "", 0);
}

// src/blocks/editor.ts
import { Value as Value3 } from "@sinclair/typebox/value";
var SCAFFOLD_MARKER_SCHEMA_PATTERN = "^\\s*\\{\\{\\s*[^{}\\n]*?\\S[^{}\\n]*?\\s*\\}\\}\\s*$";
var clone = (value) => JSON.parse(JSON.stringify(value));
function range(minimum, maximum, unit) {
  if (minimum !== void 0 && maximum !== void 0)
    return minimum === maximum ? `${minimum} ${unit}` : `${minimum}\u2013${maximum} ${unit}`;
  if (minimum !== void 0) return `at least ${minimum} ${unit}`;
  if (maximum !== void 0) return `at most ${maximum} ${unit}`;
  return void 0;
}
function blockSlotFacts(slot) {
  const facts = [];
  if (slot.required && slot.default === void 0) facts.push("Required");
  if (slot.default !== void 0)
    facts.push(`Default: \`${JSON.stringify(slot.default)}\``);
  if (slot.type === "component")
    facts.push("A component; placement stays in the definition");
  if (slot.enum)
    facts.push(
      `One of ${slot.enum.map((value) => `\`${JSON.stringify(value)}\``).join(", ")}`
    );
  const length = range(slot.minLength, slot.maxLength, "characters");
  if (length) facts.push(length);
  if (slot.maxWords !== void 0) facts.push(`at most ${slot.maxWords} words`);
  if (slot.oneLine) facts.push("one line");
  const bounds = range(slot.minimum, slot.maximum, "");
  if (bounds) facts.push(bounds.trim());
  const entries = range(slot.minItems, slot.maxItems, "entries");
  if (entries) facts.push(entries);
  if (slot.role) facts.push(`Role: ${slot.role}`);
  return facts;
}
function blockSlotMarkdown(slot) {
  return [slot.description, blockSlotFacts(slot).join(" \xB7 ")].filter(Boolean).join("\n\n");
}
function blockSlotEditorSchema(slot, componentRef) {
  let schema;
  if (slot.type === "component") {
    schema = componentRef ? {
      allOf: [
        componentRef,
        {
          properties: {
            props: {
              propertyNames: {
                not: { enum: [...BLOCK_SLOT_PLACEMENT_PROPS] },
                errorMessage: "Block placement belongs in the definition, not in a component slot."
              }
            }
          }
        }
      ]
    } : {
      type: "object",
      properties: { name: { type: "string" } },
      required: ["name"]
    };
  } else {
    const { oneLine, properties, items, ...rest } = slot;
    for (const key of ["role", "required", "maxWords", "description"])
      delete rest[key];
    schema = { ...rest };
    if (rest.minLength !== void 0 || rest.maxLength !== void 0 || rest.enum !== void 0) {
      const { minLength, maxLength, enum: choices, ...unbounded } = rest;
      schema = {
        ...unbounded,
        anyOf: [
          {
            ...minLength !== void 0 && { minLength },
            ...maxLength !== void 0 && { maxLength },
            ...choices !== void 0 && { enum: choices }
          },
          { type: "string", pattern: SCAFFOLD_MARKER_SCHEMA_PATTERN }
        ]
      };
    }
    if (oneLine) schema.pattern = "^[^\\r\\n]*$";
    if (items) schema.items = blockSlotEditorSchema(items, componentRef);
    if (properties) {
      schema.properties = Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [
          key,
          blockSlotEditorSchema(value, componentRef)
        ])
      );
      schema.required = Object.entries(properties).filter(([, value]) => value.required && value.default === void 0).map(([key]) => key);
      schema.additionalProperties = false;
    }
  }
  if (slot.description) schema.description = slot.description;
  const markdown = blockSlotMarkdown(slot);
  if (markdown) schema.markdownDescription = markdown;
  return schema;
}
function blockSlotsEditorSchema(definition, componentRef) {
  return {
    type: "object",
    additionalProperties: false,
    description: "Input values keyed by the slot names declared in the referenced block definition.",
    properties: Object.fromEntries(
      Object.entries(definition.slots).map(([key, slot]) => [
        key,
        blockSlotEditorSchema(slot, componentRef)
      ])
    ),
    required: Object.entries(definition.slots).filter(([, slot]) => slot.required && slot.default === void 0).map(([key]) => key)
  };
}
function blockInvocationPropsSchema(definitions, componentRef) {
  const names = Object.keys(definitions);
  const schema = {
    type: "object",
    additionalProperties: false,
    required: ["ref"],
    properties: {
      ref: {
        type: "string",
        minLength: 1,
        description: "Name in this document\u2019s props.blocks.",
        ...names.length && {
          anyOf: names.map((name) => ({
            const: name,
            type: "string",
            description: definitions[name].description ?? `Block "${name}", defined in this document.`
          }))
        }
      },
      slots: {
        type: "object",
        description: "Input values keyed by the slot names declared in the referenced block definition."
      }
    }
  };
  if (names.length)
    schema.allOf = names.map((name) => ({
      if: { properties: { ref: { const: name } }, required: ["ref"] },
      then: {
        properties: {
          slots: blockSlotsEditorSchema(definitions[name], componentRef)
        }
      }
    }));
  return schema;
}
function applyDocumentBlocksToSchema(schema, definitions, targets) {
  for (const target of targets) {
    const definition = schema.definitions?.[target.name];
    if (!definition) continue;
    const props = blockInvocationPropsSchema(definitions, target.componentRef);
    const seen = /* @__PURE__ */ new Set();
    const walk = (node) => {
      if (!node || typeof node !== "object" || seen.has(node)) return;
      seen.add(node);
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }
      const value = node;
      if (value.properties?.name?.const === "block" && value.properties.props) {
        value.properties.props = clone(props);
        return;
      }
      for (const [key, child] of Object.entries(value))
        if (key !== "$ref") walk(child);
    };
    walk(definition);
  }
}
function invocations(node, visit) {
  if (Array.isArray(node)) {
    node.forEach((item) => invocations(item, visit));
    return;
  }
  if (!isBlockRecord(node)) return;
  if (node.name === "block" && isBlockRecord(node.props) && typeof node.props.ref === "string")
    visit(node.props.ref, node);
  for (const value of Object.values(node)) invocations(value, visit);
}
function blockDependencies(definitions, name) {
  const order = [];
  const seen = /* @__PURE__ */ new Set([name]);
  const walk = (current) => {
    const definition = Object.prototype.hasOwnProperty.call(
      definitions,
      current
    ) ? definitions[current] : void 0;
    if (!definition) return;
    invocations(
      [definition.body, definition.section, definition.slide],
      (ref) => {
        if (seen.has(ref)) return;
        seen.add(ref);
        if (!Object.prototype.hasOwnProperty.call(definitions, ref)) return;
        walk(ref);
        order.push(ref);
      }
    );
  };
  walk(name);
  return order;
}
function exampleValue(slot, name, format) {
  if (slot.default !== void 0) return clone(slot.default);
  if (slot.enum?.length) return slot.enum[0];
  switch (slot.type) {
    case "string":
      return name;
    case "number":
    case "integer": {
      const minimum = slot.minimum ?? 0;
      return slot.maximum !== void 0 && slot.maximum < minimum ? slot.maximum : minimum;
    }
    case "boolean":
      return true;
    case "array": {
      const count = Math.min(
        Math.max(3, slot.minItems ?? 0),
        slot.maxItems ?? Number.POSITIVE_INFINITY
      );
      const item = slot.items ?? { type: "string" };
      return Array.from(
        { length: count },
        (_, index) => exampleValue(item, `${name} ${index + 1}`, format)
      );
    }
    case "object":
      return exampleSlots(slot.properties ?? {}, format);
    case "component":
      return format === "docx" ? { name: "paragraph", props: { text: name } } : { name: "text", props: { text: name } };
    default:
      return name;
  }
}
function exampleSlots(slots, format) {
  return Object.fromEntries(
    Object.entries(slots).filter(
      ([, slot]) => slot.required && slot.default === void 0 || slot.role
    ).map(([key, slot]) => [key, exampleValue(slot, key, format)])
  );
}
function blockInvocationExample(name, definition, options) {
  let found;
  if (isBlockRecord(options.document)) {
    const authored = Object.fromEntries(
      Object.entries(options.document).filter(([key]) => key !== "props")
    );
    invocations(authored, (ref, invocation) => {
      if (found || ref !== name) return;
      const props = invocation.props;
      found = {
        name: "block",
        props: {
          ref,
          ...isBlockRecord(props.slots) && { slots: clone(props.slots) }
        }
      };
    });
  }
  return found ?? {
    name: "block",
    props: {
      ref: name,
      slots: exampleSlots(definition.slots, options.format)
    }
  };
}
function blockReferencesFromDocument(document, source) {
  const definitions = readBlockDefinitions(document);
  if (!Value3.Check(BlockDefinitionsSchema, definitions) || validateBlockDefinitions(definitions, source.format).length > 0)
    return [];
  return Object.entries(definitions).map(([name, definition]) => ({
    name,
    format: source.format,
    template: source.template,
    definitionPointer: `/props/blocks/${blockPointerKey(name)}`,
    description: definition.description ?? "",
    definition,
    slotsSchema: blockSlotsJsonSchema(definition),
    example: blockInvocationExample(name, definition, {
      document,
      format: source.format
    }),
    dependencies: blockDependencies(definitions, name)
  }));
}

// src/blocks/removed-syntax.ts
var SLIDE_TEMPLATES = 'Slide templates were replaced by JSON blocks: define the shared slide content once in props.blocks and invoke it on each slide with { "name": "block", "props": { "ref": "<definition>" } }.';
var SLIDE_KEYS = {
  template: 'A slide no longer names a template: put { "name": "block", "props": { "ref": "<definition>" } } first among its children, with the definition in props.blocks.',
  placeholders: 'Placeholders became block slots: fill them in the invocation as { "name": "block", "props": { "ref": "<definition>", "slots": { \u2026 } } }.',
  layout: "Slide layouts are not read: the block a slide invokes lays it out, from its definition in props.blocks."
};
var isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
function explainRemovedBlockSyntax(document, format, errors) {
  const definitions = readBlockDefinitions(document);
  return errors.map((error) => {
    const suggestion = removedSyntaxSuggestion(
      document,
      format,
      definitions,
      error
    );
    return suggestion ? { ...error, suggestion } : error;
  });
}
function removedSyntaxSuggestion(document, format, definitions, error) {
  if (format === "pptx") {
    if (error.path === "/props/templates") return SLIDE_TEMPLATES;
    const slide = /^\/children\/\d+\/props\/(template|placeholders|layout)$/.exec(
      error.path
    );
    if (slide) return SLIDE_KEYS[slide[1]];
  }
  if (error.code === "unknown_component" && error.path.endsWith("/name")) {
    const name = blockValueAt(document, error.path);
    if (typeof name !== "string") return void 0;
    return Object.prototype.hasOwnProperty.call(definitions, name) ? `"${name}" is a JSON block this document defines, not a component: invoke it as { "name": "block", "props": { "ref": "${name}", "slots": { \u2026 } } }.` : `If "${name}" is a JSON block, copy its definition from jto://blocks into props.blocks and invoke it as { "name": "block", "props": { "ref": "${name}" } }; blocks are never components.`;
  }
  const invocationName = /^(.*)\/props\/name$/.exec(error.path);
  if (invocationName) {
    const node = blockValueAt(document, invocationName[1]);
    const value = blockValueAt(document, error.path);
    if (isRecord(node) && node.name === "block" && typeof value === "string")
      return `A block invocation names its definition with ref: { "name": "block", "props": { "ref": "${value}" } }.`;
  }
  return void 0;
}

// src/blueprints/schema.ts
import { Type as Type3 } from "@sinclair/typebox";
import { Value as Value4 } from "@sinclair/typebox/value";
var BLUEPRINT_FORMATS = ["docx", "pptx"];
var Identifier = Type3.String({
  pattern: "^[a-z][a-z0-9]*(-[a-z0-9]+)*$",
  description: "Kebab-case identifier."
});
var BlueprintPagesSchema = Type3.Object(
  {
    min: Type3.Integer({ minimum: 1 }),
    max: Type3.Integer({ minimum: 1 })
  },
  {
    additionalProperties: false,
    description: "Expected length once filled: pages for a document, slides for a deck."
  }
);
var BlueprintVariantSchema = Type3.Object(
  {
    description: Type3.String({ minLength: 1 }),
    whenToUse: Type3.String({ minLength: 1 }),
    pages: BlueprintPagesSchema,
    metadata: Type3.Optional(
      Type3.Record(Type3.String(), Type3.String(), {
        description: "Document metadata the scaffold writes; a value may be a scaffold marker."
      })
    ),
    children: Type3.Array(Type3.Unknown(), {
      minItems: 1,
      description: "The top-level children of the scaffolded document, in order: sections holding block invocations and ordinary components, every slot carrying a {{\u2026}} marker whose text is the guidance for filling it."
    })
  },
  { additionalProperties: false }
);
var BlueprintSchema = Type3.Object(
  {
    id: Identifier,
    format: Type3.Union(BLUEPRINT_FORMATS.map((f) => Type3.Literal(f))),
    title: Type3.String({ minLength: 1 }),
    description: Type3.String({ minLength: 1 }),
    whenToUse: Type3.String({ minLength: 1 }),
    theme: Type3.String({
      minLength: 1,
      description: "Recommended theme. Any theme renders the scaffold; this one is the archetype\u2019s house look."
    }),
    profile: Identifier,
    definitions: Type3.String({
      minLength: 1,
      description: "The playground template whose props.blocks the variants invoke. The scaffold copies the definitions it needs, and their dependencies, into the document."
    }),
    numbering: Type3.Union([Type3.Literal("none"), Type3.Literal("sections")], {
      description: "Whether section openers carry numbers the reader cites (01, 02, \u2026)."
    }),
    toc: Type3.Boolean({
      description: "Whether the scaffold places a table of contents."
    }),
    variants: Type3.Record(Identifier, BlueprintVariantSchema, {
      minProperties: 1,
      description: "Structural variants of the same archetype, so two documents from one brief do not look alike."
    })
  },
  { additionalProperties: false, description: "A document archetype as data." }
);
function validateBlueprint(value) {
  const issues = [...Value4.Errors(BlueprintSchema, value)].map(
    (error) => ({ path: error.path, message: error.message })
  );
  if (issues.length > 0) return issues;
  for (const [id, variant] of Object.entries(value.variants)) {
    if (variant.pages.min > variant.pages.max)
      issues.push({
        path: `/variants/${id}/pages`,
        message: `min (${variant.pages.min}) exceeds max (${variant.pages.max})`
      });
  }
  return issues;
}
function isBlueprint(value) {
  return Value4.Check(BlueprintSchema, value);
}

// src/blueprints/instantiate.ts
function registerBlueprints(format, candidates) {
  const registry = {};
  for (const candidate of candidates) {
    const issues = validateBlueprint(candidate);
    if (issues.length > 0)
      throw new Error(
        `Invalid bundled blueprint: ${issues.map((issue) => `${issue.path}: ${issue.message}`).join("; ")}`
      );
    const blueprint = candidate;
    if (blueprint.format !== format)
      throw new Error(
        `Blueprint ${blueprint.id} is not a ${format.toUpperCase()} blueprint.`
      );
    if (registry[blueprint.id])
      throw new Error(
        `Two bundled blueprints share the id "${blueprint.id}"; the later one would silently replace the earlier.`
      );
    registry[blueprint.id] = blueprint;
  }
  return registry;
}
var MARKER = /^\{\{\s*([\s\S]*?)\s*\}\}$/;
function selectVariant(blueprint, format, variantId) {
  if (blueprint.format !== format)
    throw new Error(
      `Blueprint ${blueprint.id} is a ${blueprint.format} blueprint; this instantiates ${format.toUpperCase()} ones.`
    );
  const id = variantId ?? Object.keys(blueprint.variants)[0];
  const variant = blueprint.variants[id];
  if (!variant)
    throw new Error(
      `Blueprint ${blueprint.id} has no variant "${id}"; it has ${Object.keys(
        blueprint.variants
      ).join(", ")}.`
    );
  return { id, variant };
}
function instantiateBlueprint(blueprint, options, root) {
  const { id: variantId, variant } = selectVariant(
    blueprint,
    root.format,
    options.variant
  );
  const children = structuredClone(
    options.children ?? variant.children
  );
  const blocks = definitionsFor(children, options.definitions, blueprint);
  const metadata2 = { ...variant.metadata, ...options.metadata };
  const props = {
    theme: options.theme ?? blueprint.theme,
    qualityProfile: blueprint.profile,
    ...root.props,
    ...Object.keys(blocks).length > 0 && { blocks },
    ...root.metadataPointer === "/props" ? metadata2 : { metadata: metadata2 }
  };
  const document = {
    name: root.format,
    props,
    children
  };
  return {
    document,
    fillMap: fillMap(document, blocks, root),
    variant: variantId
  };
}
function definitionsFor(children, available, blueprint) {
  const refs = /* @__PURE__ */ new Set();
  const visit = (node) => {
    if (Array.isArray(node)) return node.forEach(visit);
    if (!node || typeof node !== "object") return;
    const record = node;
    const props = record.props;
    if (record.name === "block" && typeof props?.ref === "string")
      refs.add(props.ref);
    Object.values(record).forEach(visit);
  };
  visit(children);
  const result = {};
  for (const ref of refs) {
    if (!available[ref])
      throw new Error(
        `Blueprint ${blueprint.id} invokes "${ref}", which ${blueprint.definitions} does not define.`
      );
    for (const name of [...blockDependencies(available, ref), ref])
      result[name] ??= structuredClone(available[name]);
  }
  return result;
}
function fillMap(document, blocks, root) {
  const metadataPrefix = `${root.metadataPointer}/`;
  return root.markers(document).map((occurrence) => {
    const guidance = occurrence.text.match(MARKER)?.[1] ?? occurrence.text;
    const base = { path: occurrence.path, marker: occurrence.text, guidance };
    if (occurrence.path.startsWith(metadataPrefix) && !occurrence.path.slice(metadataPrefix.length).includes("/"))
      return { ...base, kind: "metadata" };
    const slot = slotAt(document, occurrence.path, blocks);
    return slot ? { ...base, kind: "slot", ...slot } : { ...base, kind: "text" };
  });
}
function slotAt(document, pointer2, blocks) {
  const at = pointer2.lastIndexOf("/props/slots/");
  if (at < 0) return void 0;
  const invocation = valueAt(document, pointer2.slice(0, at));
  const ref = invocation?.props?.ref;
  if (typeof ref !== "string" || !blocks[ref]) return void 0;
  const segments = pointer2.slice(at + "/props/slots/".length).split("/").map(unescape);
  let slot = blocks[ref].slots[segments[0]];
  const names = [segments[0]];
  for (const segment of segments.slice(1)) {
    if (!slot) break;
    if (slot.type === "array" && /^\d+$/.test(segment)) slot = slot.items;
    else if (slot.type === "object") {
      slot = slot.properties?.[segment];
      names.push(segment);
    } else if (slot.type === "component")
      break;
    else slot = void 0;
  }
  if (!slot) return void 0;
  return {
    block: ref,
    slot: names.join("."),
    type: slot.type,
    ...slot.maxWords !== void 0 && { maxWords: slot.maxWords },
    ...slot.maxLength !== void 0 && { maxLength: slot.maxLength },
    ...slot.oneLine !== void 0 && { oneLine: slot.oneLine },
    ...slot.required !== void 0 && { required: slot.required }
  };
}
function unescape(segment) {
  return segment.replace(/~1/g, "/").replace(/~0/g, "~");
}
function valueAt(root, pointer2) {
  if (pointer2 === "") return root;
  let current = root;
  for (const segment of pointer2.split("/").slice(1).map(unescape)) {
    if (Array.isArray(current)) current = current[Number(segment)];
    else if (current && typeof current === "object")
      current = current[segment];
    else return void 0;
  }
  return current;
}

// src/blueprints/rubric.ts
var RUBRIC = [
  {
    level: 1,
    name: "Integrity",
    bar: "Nothing is clipped, overflowing, overlapping, empty or left as placeholder text. Every page renders what the author meant to put there."
  },
  {
    level: 2,
    name: "Legibility and accessibility",
    bar: "Type is large enough to read at the medium it is for, contrast holds, and the reading order is obvious."
  },
  {
    level: 3,
    name: "Visual coherence",
    bar: "Hierarchy, grid, rhythm, typography and colour agree across every page. Repeated elements repeat. Nothing looks hand-placed."
  },
  {
    level: 4,
    name: "Communicative effectiveness",
    bar: "Density, structure, charts and tables fit the purpose. Titles say what the page concludes. Every chart has a takeaway and a source; every number has a unit."
  },
  {
    level: 5,
    name: "Craft and distinctiveness",
    bar: "The composition is refined and intentional, and does not read as a template with the blanks filled in."
  }
];
var SHIPPING_QUESTION = "Would you send this to a client, unchanged, with your name on it?";
var GENERICNESS_PENALTY = "Separately, rate how generic this looks: whether it reads as a document made for this brief, or as a template that would look identical for any other. A document can be clean, coherent and still generic.";

// src/utils/deepMerge.ts
function isObject(item) {
  return item !== null && typeof item === "object" && !Array.isArray(item);
}
function deepMerge(target, source) {
  const output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target) || !isObject(target[key])) {
          output[key] = source[key];
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        output[key] = source[key];
      }
    });
  }
  return output;
}
function mergeWithDefaults(userConfig, themeDefaults) {
  return deepMerge(themeDefaults, userConfig);
}

// src/utils/serviceUrl.ts
function isPrivateServiceUrl(url) {
  let hostname;
  try {
    hostname = new URL(
      /^https?:\/\//i.test(url.trim()) ? url.trim() : `http://${url.trim()}`
    ).hostname.toLowerCase();
  } catch {
    return false;
  }
  const host = hostname.replace(/^\[|\]$/g, "").replace(/\.$/, "");
  if (host === "localhost" || host.endsWith(".localhost")) return true;
  if (host === "::1" || /^f[cd][0-9a-f]{2}:/.test(host)) return true;
  if (/^fe[89ab][0-9a-f]:/.test(host)) return true;
  const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(host);
  if (v4) {
    const [a, b] = [Number(v4[1]), Number(v4[2])];
    return a === 127 || a === 10 || a === 172 && b >= 16 && b <= 31 || a === 192 && b === 168 || a === 169 && b === 254;
  }
  return /\.(local|internal|home\.arpa)$/.test(host);
}
var REMOTE_EXPORT_WARNING = "W_HIGHCHARTS_REMOTE_EXPORT";
function remoteExportNotice(serverUrl, allowRemote) {
  if (isPrivateServiceUrl(serverUrl)) return void 0;
  if (!allowRemote) {
    throw new Error(
      `Highcharts export server ${serverUrl} is outside this machine and its private networks, and a chart is posted whole \u2014 every series, label and title. Run the export server locally (pnpm dlx highcharts-export-server --enableServer true), or set services.highcharts.allowRemote (HIGHCHARTS_ALLOW_REMOTE=1) to send chart data there deliberately.`
    );
  }
  return `Chart data \u2014 every series, label and title \u2014 was sent to ${serverUrl}, outside this machine and its private networks (services.highcharts.allowRemote).`;
}

// src/utils/serviceClient.ts
var DEFAULT_TIMEOUT_MS = 3e4;
var DEFAULT_RETRIES = 2;
var DEFAULT_RETRY_BASE_DELAY_MS = 250;
var MAX_RETRY_DELAY_MS = 2e3;
function resolveServiceUrl(propsUrl, servicesUrl, defaultUrl) {
  const raw = (propsUrl || servicesUrl || defaultUrl).trim();
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
  return withScheme.replace(/\/+$/, "");
}
var SERVICE_UNAVAILABLE_CODE = "SERVICE_UNAVAILABLE";
function serviceUnavailable(message) {
  return Object.assign(new Error(message), { code: SERVICE_UNAVAILABLE_CODE });
}
function isRetryable(error) {
  const { code, status } = error;
  if (code === SERVICE_UNAVAILABLE_CODE) return true;
  return status === 429 || status !== void 0 && status >= 500;
}
function withAttemptCount(error, attempts) {
  const next = new Error(`${error.message} (after ${attempts} attempts)`);
  const { code, status } = error;
  if (code !== void 0) Object.assign(next, { code });
  if (status !== void 0) Object.assign(next, { status });
  return next;
}
function backoffMs(attempt, baseDelayMs) {
  const ceiling = Math.min(
    baseDelayMs * 2 ** (attempt - 1),
    MAX_RETRY_DELAY_MS
  );
  return Math.round(ceiling * (0.5 + Math.random() * 0.5));
}
function transportFailure(error, opts, timeoutMs) {
  if (error?.name === "AbortError") {
    return serviceUnavailable(
      `${opts.serviceLabel} timed out after ${timeoutMs}ms at ${opts.url}.`
    );
  }
  const cause = error instanceof Error ? error.message : String(error);
  return serviceUnavailable(opts.onUnreachable(opts.url, cause));
}
async function exchange(opts, headers, signal, timeoutMs) {
  let response;
  try {
    response = await fetch(`${opts.url}${opts.path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(opts.body),
      signal
    });
  } catch (error) {
    throw transportFailure(error, opts, timeoutMs);
  }
  if (!response.ok) {
    await response.body?.cancel().catch(() => void 0);
    throw Object.assign(
      new Error(
        `${opts.serviceLabel} returned ${response.status}: ${response.statusText}`
      ),
      { status: response.status }
    );
  }
  try {
    return await opts.decode(response);
  } catch (error) {
    throw transportFailure(error, opts, timeoutMs);
  }
}
async function attemptPost(opts, headers, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await exchange(opts, headers, controller.signal, timeoutMs);
  } finally {
    clearTimeout(timer);
  }
}
async function postJsonToService(opts) {
  const resolvedHeaders = typeof opts.headers === "function" ? await opts.headers(opts.body) : opts.headers;
  const headers = {
    "Content-Type": "application/json",
    ...resolvedHeaders
  };
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const baseDelayMs = opts.retryBaseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS;
  const attempts = 1 + Math.max(0, opts.retries ?? DEFAULT_RETRIES);
  for (let attempt = 1; ; attempt++) {
    try {
      return await attemptPost(opts, headers, timeoutMs);
    } catch (error) {
      const failure = error;
      if (attempt >= attempts || !isRetryable(failure)) {
        throw attempt === 1 ? failure : withAttemptCount(failure, attempt);
      }
      opts.onRetry?.(attempt, failure);
      await new Promise(
        (resolve) => setTimeout(resolve, backoffMs(attempt, baseDelayMs))
      );
    }
  }
}

// src/utils/promiseLimiter.ts
function createLimiter(max) {
  let active = 0;
  const queue = [];
  const release = () => {
    active--;
    queue.shift()?.();
  };
  return async function limit(fn) {
    while (active >= max)
      await new Promise((resolve) => queue.push(resolve));
    active++;
    try {
      return await fn();
    } finally {
      release();
    }
  };
}

// src/utils/chartRequests.ts
var DEFAULT_CHART_CONCURRENCY = 4;
var stats = {
  collected: 0,
  unique: 0,
  retries: 0,
  maxInFlight: 0
};
var inFlight = 0;
function getChartRequestStats() {
  return { ...stats };
}
function resetChartRequestStats() {
  stats.collected = 0;
  stats.unique = 0;
  stats.retries = 0;
  stats.maxInFlight = 0;
}
function recordChartCollected() {
  stats.collected++;
}
function recordChartRetry() {
  stats.retries++;
}
var gates = /* @__PURE__ */ new Map();
function gateFor(serverUrl, concurrency) {
  const existing = gates.get(serverUrl);
  if (existing) return existing;
  const max = Math.max(
    1,
    Math.floor(
      Number.isFinite(concurrency) ? concurrency : DEFAULT_CHART_CONCURRENCY
    )
  );
  const gate = createLimiter(max);
  gates.set(serverUrl, gate);
  return gate;
}
function limitChartRequest(serverUrl, concurrency, fn) {
  return gateFor(serverUrl, concurrency)(fn);
}
function chartRequestKey(serverUrl, requestBody) {
  return `${serverUrl}
${JSON.stringify(requestBody)}`;
}
function dedupeChartRequest(cache, key, send) {
  if (!cache) return send();
  const pending = cache.get(key);
  if (pending) return pending;
  const started = send();
  cache.set(key, started);
  return started;
}
function sendChartRequest(request) {
  return dedupeChartRequest(
    request.cache,
    chartRequestKey(request.serverUrl, request.requestBody),
    () => limitChartRequest(
      request.serverUrl,
      request.concurrency,
      () => counted(request.send)
    )
  );
}
async function counted(send) {
  stats.unique++;
  inFlight++;
  stats.maxInFlight = Math.max(stats.maxInFlight, inFlight);
  try {
    return await send();
  } finally {
    inFlight--;
  }
}
function resetChartLimiters() {
  gates.clear();
}
export {
  ASSET_UNREADABLE,
  BLOCK_SLOT_PLACEMENT_PROPS,
  BLOCK_SLOT_ROLES,
  BLUEPRINT_FORMATS,
  BlockDefinitionsSchema,
  BlockEvaluationError,
  BlockInvocationPropsSchema,
  BlockSlotSchema,
  BlueprintPagesSchema,
  BlueprintSchema,
  BlueprintVariantSchema,
  CANVASES,
  ChromeSchema,
  ComponentValidationError,
  DEFAULT_CHART_CONCURRENCY,
  DEFAULT_CHART_THEME_COLORS,
  DEFAULT_ERROR_CONFIG,
  DEFAULT_VISUAL_DPI,
  DesignSpacingSchema,
  DesignSystemProperties,
  DesignSystemSchema,
  DuplicateComponentError,
  ERROR_EMOJIS,
  FONT_URL_ALLOWLIST,
  FeatureRequirementCollector,
  FontFamilyNameSchema,
  FontRegistry,
  FontRegistryEntrySchema,
  FontRegistrySchema,
  FontSourceSchema,
  GENERICNESS_PENALTY,
  HighchartsThemeDefaultsSchema,
  JsonBlockDefinitionSchema,
  JsonBlockEvaluator,
  MAX_RASTERIZE_BATCH_SLIDES,
  MAX_RASTERIZE_FONTS,
  MAX_RASTERIZE_FONT_BYTES,
  MAX_VISUAL_DPI,
  MIN_VISUAL_DPI,
  MotifSchema,
  POINTS_PER_PIXEL_96DPI,
  POPULAR_GOOGLE_FONTS,
  PaletteSchema,
  REMOTE_EXPORT_WARNING,
  RENDERER_DEPENDENCY_MISSING,
  ROLE_SCALE_STEPS,
  RUBRIC,
  RendererRegistry,
  SAFE_FONTS,
  SERVICE_UNAVAILABLE_CODE,
  SHIPPING_QUESTION,
  TYPE_ROLES,
  TextCaseSchema,
  TypeRoleNameSchema,
  TypeRoleSchema,
  TypeRolesSchema,
  TypographySchema,
  UPSTREAM_OVERRIDES,
  UnknownPreservedComponentError,
  UnsupportedRendererFeatureError,
  WEIGHT_LABELS,
  applyDocumentBlocksToSchema,
  applyExportMode,
  applyFontSubstitution,
  assertNever,
  assertRendererSupports,
  assetUnreadableError,
  blockDependencies,
  blockInvocationExample,
  blockInvocationPropsSchema,
  blockPointerKey,
  blockReferencesFromDocument,
  blockSlotBudgets,
  blockSlotEditorSchema,
  blockSlotFacts,
  blockSlotJsonSchema,
  blockSlotMarkdown,
  blockSlotRoles,
  blockSlotsEditorSchema,
  blockSlotsJsonSchema,
  blockValueAt,
  blockWordCount,
  buildDefaultSubstitutionMap,
  calculatePosition,
  capsFormatting,
  chartFamilyResolver,
  chartFontFaceCss,
  chartPointsPerPixel,
  clampVisualDpi,
  clearComponentNamesCache,
  collectFontNamesFromDocx,
  collectFontNamesFromPptx,
  compareSemver,
  composeBlocksWithPlugins,
  convertToJsonSchema,
  createBlockAuthoringSchema,
  createComponent,
  createComponentSchema,
  createComponentSchemaObject,
  createErrorConfig,
  createJsonParseError,
  createLimiter,
  createVersion,
  cssFontFamily,
  defaultSubstituteFor,
  definitionsFor,
  designCanvas,
  designColors,
  detectFontFormat,
  diagnoseUnsupportedFeatures,
  documentBlockMetadata,
  documentFontRegistry,
  explainRemovedBlockSyntax,
  exportSchemaToFile,
  extractStandardComponentNames,
  fetchGoogleFontSources,
  fillChartOptions,
  fixSchemaReferences,
  formatErrorMessage,
  formatErrorSummary,
  getChartRequestStats,
  getLiteralValue,
  getObjectSchemaPropertyNames,
  getSchemaMetadata,
  getUpstreamOverride,
  getValidationSummary,
  groupErrorsByPath,
  instantiateBlueprint,
  isAllowedFontUrl,
  isBlockRecord,
  isBlueprint,
  isLiteralSchema,
  isObjectSchema,
  isPrivateServiceUrl,
  isSafeFont,
  isUnionSchema,
  isValidSemver,
  isValidationSuccess,
  latestVersion,
  mergeFontRegistries,
  mergeWithDefaults,
  parseSemver,
  partitionDiagnostics,
  postJsonToService,
  readBlockDefinitions,
  recordChartCollected,
  recordChartRetry,
  registerBlueprints,
  remoteExportNotice,
  rendererError,
  rendererWarning,
  resetChartLimiters,
  resetChartRequestStats,
  resolveBlockSlot,
  resolveComponentVersion,
  resolveDesignColor,
  resolveMotif,
  resolveServiceUrl,
  resolveTypeRoles,
  restructureNameDiscriminatedUnions,
  rewriteFontFamilyName,
  selectVariant,
  sendChartRequest,
  slotAt,
  synthesizeFamilyName,
  themeFontRegistry,
  toAuthoredBlockPointer,
  transformValueError,
  transformValueErrors,
  typeScaleSizes,
  unionBranches,
  validateBlockDefinitions,
  validateBlockInvocations,
  validateBlueprint,
  validateCustomComponentProps,
  validateDesignColors,
  validateFontReferences,
  valueAt,
  withChartFontFaceCss,
  withChartPresets,
  withChartTypography
};
//# sourceMappingURL=index.js.map